import {
    symbolLayoutAttributes,
    symbolGlobeExtAttributes,
    collisionVertexAttributes,
    collisionVertexAttributesExt,
    collisionBoxLayout,
    dynamicLayoutAttributes,
    placementFadeAttributes,
    iconTransitioningAttributes,
    zOffsetAttributes,
    orientationAttributes,
    featureIdAttributes
} from './symbol_attributes';
import {SymbolLayoutArray,
    SymbolGlobeExtArray,
    SymbolDynamicLayoutArray,
    SymbolOpacityArray,
    SymbolFadeArray,
    CollisionBoxLayoutArray,
    CollisionVertexExtArray,
    CollisionVertexArray,
    PlacedSymbolArray,
    SymbolInstanceArray,
    GlyphOffsetArray,
    SymbolLineVertexArray,
    SymbolIconTransitioningArray,
    ZOffsetVertexArray,
    StructArrayLayout1f4
} from '../array_types';
import {SymbolPropertyBinderUBO} from './symbol_property_binder_ubo';
import ONE_EM from '../../symbol/one_em';
import Point from '@mapbox/point-geometry';
import SegmentVector, {type Segment} from '../segment';
import {ProgramConfigurationSet} from '../program_configuration';
import {TriangleIndexArray, LineIndexArray} from '../index_array_type';
import transformText from '../../symbol/transform_text';
import mergeLines from '../../symbol/mergelines';
import {allowsVerticalWritingMode, stringContainsRTLText} from '../../util/script_detection';
import {WritingMode, fitIconToText, shapeIcon, type Shaping} from '../../symbol/shaping_shared';
import {loadRenderGeometry} from '../load_geometry';
import toEvaluationFeature, {type EvaluationFeature} from '../evaluation_feature';
import {VectorTileFeature} from '@mapbox/vector-tile';
const vectorTileFeatureTypes = VectorTileFeature.types;
import {verticalizedCharacterMap} from '../../util/verticalize_punctuation';
import {evaluateSizeForFeature, evaluateSizeForZoom, getSizeData, SIZE_PACK_FACTOR} from '../../symbol/symbol_size';
import {computeFontScale, getAppearanceIconValues, getAppearanceTextValues, getScaledImageVariant, packSizeForVertex, rotatesInTileSpace, MAX_PACKED_SIZE} from '../../symbol/symbol_layout_shared';
import {register} from '../../util/web_worker_transfer';
import EvaluationParameters from '../../style/evaluation_parameters';
import Formatted from '../../style-spec/expression/types/formatted';
import ResolvedImage from '../../style-spec/expression/types/resolved_image';
import {ImageVariant as ImageVariantClass} from '../../style-spec/expression/types/image_variant';
import {rtl} from '../../symbol/rtl_text';
import {resamplePred} from '../../geo/projection/resample';
import tileTransform, {getNorthOffset} from '../../geo/projection/tile_transform';
import {tileCoordToECEF, globeToMercatorTransition} from '../../geo/projection/globe_util';
import {getProjection} from '../../geo/projection/index';
import {mat4, vec3, vec4} from 'gl-matrix';
import assert from '../../style-spec/util/assert';
import {regionsEquals, skipClipping, pointInFootprint, transformPointToTile} from '../../../3d-style/source/replacement_source';
import {LayerTypeMask} from '../../../3d-style/util/conflation';
import {clamp, warnOnce, wrap} from '../../util/util';
import {number as mix} from '../../style-spec/util/interpolate';
import EXTENT from '../../style-spec/data/extent';
import {makeFQID} from '../../util/fqid';
import {xyTransformMat4} from '../../util/mat4';
import {Elevation} from '../../terrain/elevation';
import {getFogOpacityAtTileCoord, FOG_SYMBOL_CLIPPING_THRESHOLD} from '../../style/fog_helpers';
import {MIN_COLLISION_PERSPECTIVE_RATIO} from '../../geo/projection/projection_util';
import {SymbolIdOrigin, SymbolPlacementType, SymbolVariantVisibility, hasStableId} from '../../placement/types';
import {defaultPlacementRules} from '../../placement/placement_rules';
import {subgroupOrderForLayerPosition} from '../../placement/symbol_placement_parameters';
import {type CollisionBoxArray, type CollisionBox, type SymbolInstance, SymbolOrientationArray} from '../array_types';
import {type SymbolQuad, getIconQuads, getGlyphQuads} from '../../symbol/quads';
import {FeatureAppearances} from './feature_appearances';

import type {VectorTileLayer} from '@mapbox/vector-tile';
import type Anchor from '../../symbol/anchor';
import type {Region, ReplacementSource} from '../../../3d-style/source/replacement_source';
import type {PossiblyEvaluatedPropertyValue, PropertyValue} from '../../style/properties';
import type SymbolStyleLayer from '../../style/style_layer/symbol_style_layer';
import type {Class} from '../../types/class';
import type {ProjectionSpecification} from '../../style-spec/types';
import type Projection from '../../geo/projection/projection';
import type {CanonicalTileID, OverscaledTileID, UnwrappedTileID} from '../../source/tile_id';
import type {
    Bucket,
    BucketParameters,
    IndexedFeature,
    PopulateParameters
} from '../bucket';
import type {StructArray, StructArrayMember} from '../../util/struct_array';
import type Context from '../../gl/context';
import type IndexBuffer from '../../gl/index_buffer';
import type VertexBuffer from '../../gl/vertex_buffer';
import type {SizeData, InterpolatedSize} from '../../symbol/symbol_size';
import type {FeatureStates} from '../../source/source_state';
import type {TileTransform} from '../../geo/projection/tile_transform';
import type {TileFootprint} from '../../../3d-style/util/conflation';
import type {LUT} from '../../util/lut';
import type {SpritePositions} from '../../util/image';
import type {TypedStyleLayer} from '../../style/style_layer/typed_style_layer';
import type {ElevationType} from '../../../3d-style/elevation/elevation_constants';
import type {ImageId} from '../../style-spec/expression/types/image_id';
import type {SymbolHDExtension} from '../../../3d-style/data/bucket/symbol_hd_extension';
import type {ImageVariant, StringifiedImageVariant} from '../../style-spec/expression/types/image_variant';
import type {ImagePositionMap} from '../../render/image_atlas';
import type {StyleImage} from '../../style/style_image';
import type SymbolAppearance from '../../style/appearance';
import type {AppearanceProps} from '../../style/appearance_properties';
import type {FeatureState, GlobalProperties} from '../../style-spec/expression';
import type ImageManager from '../../render/image_manager';
import type {AppearanceUpdateResult} from './feature_appearances';
import type {SymbolSource} from '../../placement/symbol_source';
import type {SymbolVariantId, SymbolId, TileCoverageRect} from '../../placement/types';
import type {GlobalPlacement} from '../../placement/global_placement';
import type {TileIdentity} from '../../placement/placement_debug';
import type {GlobalPlacementPriority} from '../../placement/global_placement_priority';
import type {SymbolIdRangeAllocator} from '../../placement/symbol_id_range_allocator';
import type {PlacementGroupOrders} from '../../placement/symbol_placement_parameters';
import type Transform from '../../geo/transform';
import type Tile from '../../source/tile';
import type {FogState} from '../../style/fog_helpers';

export type AppearanceFeatureData = {
    // Identity — set during populate() on the worker thread, never mutated afterwards.
    id?: string | number | null;
    properties: Record<PropertyKey, unknown>;
    usesAppearanceIconAsPlaceholder: boolean;

    // Cached vertex snapshots — written on first appearance activation, restored on deactivation.
    // Optional because these are only ever assigned on the main thread; the
    // worker doesn't need to serialize empty placeholders.
    layoutBasedIconVertexData?: Uint16Array;
    layoutBasedIconTransitioningVertexData?: Uint16Array;
    layoutBasedTextVertexData?: Uint16Array;

    // Mutable render state — updated every frame by updateAppearances() on the main thread.
    // Optional: absent ≡ false (walker-skipped on transfer). All readers either
    // use a truthy check or a `!==` comparison, both of which behave identically
    // whether the field is missing, undefined, or explicitly false.
    isUsingAppearanceIconVertexData?: boolean;
    isUsingAppearanceTextVertexData?: boolean;
    // Index into layer.appearances[]; -1 means no appearance is active (default rendering).
    // Optional: absent on first read ≡ no previous value, matching the purpose
    // of the former `UNINITIALIZED_APPEARANCE_INDEX` sentinel.
    activeAppearanceIndex?: number;

    // Layout dependencies — written during worker-thread layout (addTextVertices), read on main thread.
    textShaping?: Shaping;
    iconTextFitPadding?: [number, number, number, number];
    fontScale?: number;
    textScaleFactor?: number;
};

// Per-frame context passed to updateSymbolInstanceIconVertices.
// Groups the parameters that are fixed for the entire updateAppearances() call
// or evaluated once per feature from layout, separating them from per-instance
// identifiers (symbolInstance, featureData, activeAppearance, evaluationFeature, vertexOffset).
type AppearanceIconUpdateContext = {
    // Shared fields (same order in both context types)
    canonical: CanonicalTileID;
    layer: SymbolStyleLayer;
    featureState: FeatureState;
    availableImages: Array<ImageId>;
    // Icon-specific fields
    globalProperties: GlobalProperties;
    iconScaleFactor: number;
    layoutIconOffset: [number, number];
    layoutIconSize: number;
    layoutIconRotate: number;
    iconRotatesInTileSpace: boolean;
};

// Per-frame context passed to updateSymbolInstanceTextVertices.
type AppearanceTextUpdateContext = {
    // Shared fields (same order in both context types)
    canonical: CanonicalTileID;
    layer: SymbolStyleLayer;
    featureState: FeatureState;
    availableImages: Array<ImageId>;
    // Text-specific fields
    textScaleFactor: number;
    imageMap: Map<StringifiedImageVariant, StyleImage>;
    layoutTextOffset: [number, number];
    layoutTextSize: number;
    layoutTextRotate: number;
    textRotatesInTileSpace: boolean;
    layoutMinZoomSize: number;
    layoutMaxZoomSize: number;
    layoutTextSizeMinZoom: number;
    layoutTextSizeMaxZoom: number;
};

export type SingleCollisionBox = {
    x1: number;
    y1: number;
    x2: number;
    y2: number;
    padding: number;
    projectedAnchorX: number;
    projectedAnchorY: number;
    projectedAnchorZ: number;
    tileAnchorX: number;
    tileAnchorY: number;
    elevation?: number;
    tileID?: OverscaledTileID;
};

export type CollisionArrays = {
    textBox?: SingleCollisionBox;
    verticalTextBox?: SingleCollisionBox;
    iconBox?: SingleCollisionBox;
    verticalIconBox?: SingleCollisionBox;
    textFeatureIndex?: number;
    verticalTextFeatureIndex?: number;
    iconFeatureIndex?: number;
    verticalIconFeatureIndex?: number;
};

const createCollisionBox = (): SingleCollisionBox => ({
    x1: 0, y1: 0, x2: 0, y2: 0, padding: 0, projectedAnchorX: 0, projectedAnchorY: 0, projectedAnchorZ: 0,
    tileAnchorX: 0, tileAnchorY: 0, elevation: undefined, tileID: undefined
});
const scratchTextBox = createCollisionBox();
const scratchVerticalTextBox = createCollisionBox();
const scratchIconBox = createCollisionBox();
const scratchVerticalIconBox = createCollisionBox();
const scratchCollisionArrays: CollisionArrays = {};

function readCollisionBox(collisionBoxArray: CollisionBoxArray, index: number, box: SingleCollisionBox): number {
    const b = collisionBoxArray.get(index);
    box.x1 = b.x1;
    box.y1 = b.y1;
    box.x2 = b.x2;
    box.y2 = b.y2;
    box.padding = b.padding;
    box.projectedAnchorX = b.projectedAnchorX;
    box.projectedAnchorY = b.projectedAnchorY;
    box.projectedAnchorZ = b.projectedAnchorZ;
    box.tileAnchorX = b.tileAnchorX;
    box.tileAnchorY = b.tileAnchorY;
    return b.featureIndex;
}

export type SymbolFeature = {
    sortKey: number | undefined;
    text: Formatted | undefined;
    icon: ResolvedImage | null | undefined;
    index: number;
    sourceLayerIndex: number;
    geometry: Array<Array<Point>>;
    properties: VectorTileFeature["properties"];
    type: 'Unknown' | 'Point' | 'LineString' | 'Polygon';
    id?: number | string | undefined;
};

export type SortKeyRange = {
    sortKey: number;
    symbolInstanceStart: number;
    symbolInstanceEnd: number;
};

type LineVertexRange = {
    lineLength: number;
    lineStartIndex: number;
};

type GlobeData = {
    anchor: Anchor;
    up: vec3;
};

// Opacity arrays are frequently updated but don't contain a lot of information, so we pack them
// tight. Each Uint32 is actually four duplicate Uint8s for the four corners of a glyph
// 7 bits are for the current opacity, and the lowest bit is the target opacity

// actually defined in symbol_attributes.js
// const placementOpacityAttributes = [
//     { name: 'a_fade_opacity', components: 1, type: 'Uint32' }
// ];
const shaderOpacityAttributes: ReadonlyArray<StructArrayMember> = [
    {name: 'a_fade_opacity', components: 1, type: 'Uint8', offset: 0}
];

function addVertex(array: SymbolLayoutArray, tileAnchorX: number, tileAnchorY: number, ox: number, oy: number, tx: number, ty: number, sizeVertex: number[], isSDF: boolean, pixelOffsetX: number, pixelOffsetY: number, minFontScaleX: number, minFontScaleY: number) {
    const aSizeX = sizeVertex ? Math.min(MAX_PACKED_SIZE, Math.round(sizeVertex[0])) : 0;
    const aSizeY = sizeVertex ? Math.min(MAX_PACKED_SIZE, Math.round(sizeVertex[1])) : 0;

    array.emplaceBack(
        // a_pos_offset
        tileAnchorX,
        tileAnchorY,
        Math.round(ox * 32),
        Math.round(oy * 32),

        // a_data
        tx, // x coordinate of symbol on atlas texture
        ty, // y coordinate of symbol on atlas texture
        (aSizeX << 1) + (isSDF ? 1 : 0),
        (aSizeY << 1) + 0,  // pack isAppearanceIcon flag in last bit, 0 for non-appearance icon
        pixelOffsetX * 16,
        pixelOffsetY * 16,
        minFontScaleX * 256,
        minFontScaleY * 256
    );
}

function addTransitioningVertex(array: SymbolIconTransitioningArray, tx: number, ty: number) {
    array.emplaceBack(tx, ty);
}

function addGlobeVertex(array: SymbolGlobeExtArray, projAnchorX: number, projAnchorY: number, projAnchorZ: number, normX: number, normY: number, normZ: number) {
    array.emplaceBack(
        // a_globe_anchor
        projAnchorX,
        projAnchorY,
        projAnchorZ,

        // a_globe_normal
        normX,
        normY,
        normZ
    );
}

function updateGlobeVertexNormal(array: SymbolGlobeExtArray, vertexIdx: number, normX: number, normY: number, normZ: number) {
    // Modify float32 array directly. 20 bytes per entry, 3xInt16 for position, 3xfloat32 for normal
    const offset = vertexIdx * 5 + 2;
    array.float32[offset + 0] = normX;
    array.float32[offset + 1] = normY;
    array.float32[offset + 2] = normZ;
}

function addDynamicAttributes(dynamicLayoutVertexArray: StructArray, x: number, y: number, z: number, angle: number) {
    dynamicLayoutVertexArray.emplaceBack(x, y, z, angle);
    dynamicLayoutVertexArray.emplaceBack(x, y, z, angle);
    dynamicLayoutVertexArray.emplaceBack(x, y, z, angle);
    dynamicLayoutVertexArray.emplaceBack(x, y, z, angle);
}

function containsRTLText(formattedText: Formatted): boolean {
    for (const section of formattedText.sections) {
        if (stringContainsRTLText(section.text)) {
            return true;
        }
    }
    return false;
}

type ProjectedAnchor = {
    x: number;
    y: number;
    // Perspective foreshortening under pitch: distant anchors get a smaller ratio, matching the
    // per-frame scaling the symbol shader applies. Screen density is deliberately not part of it.
    perspectiveRatio: number;
    occluded: boolean;
};

// Projects a tile-space anchor through the tile's posMatrix into logical (density-independent)
// viewport pixels, mirroring CollisionIndex#projectAndGetPerspectiveRatio minus the legacy grid's
// viewport-padding offset (the new placement grid, see src/placement/collision_grid.ts, expects
// unpadded coordinates).
function projectAnchor(posMatrix: mat4, x: number, y: number, z: number, transform: Transform, bucketProjection: Projection, checkOcclusion: boolean, fogState: FogState | null | undefined, unwrappedTileID: UnwrappedTileID): ProjectedAnchor {
    const p: [number, number, number, number] = [x, y, z, 1];
    let behindFog = false;
    if (z || transform.pitch > 0) {
        vec4.transformMat4(p, p, posMatrix);
        if (fogState && bucketProjection.name !== 'globe') {
            const fogOpacity = getFogOpacityAtTileCoord(fogState, x, y, z, unwrappedTileID, transform);
            behindFog = fogOpacity > FOG_SYMBOL_CLIPPING_THRESHOLD;
        }
    } else {
        xyTransformMat4(p, p, posMatrix);
    }
    const w = p[3];
    return {
        x: ((p[0] / w + 1) / 2) * transform.width,
        y: ((-p[1] / w + 1) / 2) * transform.height,
        perspectiveRatio: Math.min(0.5 + 0.5 * (transform.getCameraToCenterDistance(bucketProjection) / w), 1.5),
        occluded: (checkOcclusion && p[2] > w) || behindFog,
    };
}

// Number of uint16 values per vertex in SymbolLayoutArray.
// Layout: [tileAnchorX, tileAnchorY, ox, oy, tx, ty, aSizeX, aSizeY, pixelOffsetX, pixelOffsetY, minFontScaleX, minFontScaleY]
const SYMBOL_VERTEX_STRIDE = 12;

// Number of uint16 values per vertex in SymbolIconTransitioningArray (a_texb: [tx, ty]).
const ICON_TRANSITIONING_STRIDE = 2;

const FADE_TARGET_VISIBLE = 1;
const FADE_SETTLED = 2;
const FADE_SETTLED_HIDDEN = FADE_SETTLED;

function fadeState(target: boolean, settled: boolean): number {
    return (target ? FADE_TARGET_VISIBLE : 0) | (settled ? FADE_SETTLED : 0);
}

function evaluateFadeOpacity(refTime: number, target: boolean, settled: boolean, now: number, fadeDuration: number): number {
    if (settled || fadeDuration === 0) return target ? 1 : 0;
    const t = (now - refTime) / fadeDuration;
    return target ? clamp(t, 0, 1) : clamp(1 - t, 0, 1);
}

const EMPTY_FEATURE_STATE: FeatureState = {};
const textZOffsetScratch = new ZOffsetVertexArray();
const iconZOffsetScratch = new ZOffsetVertexArray();

function appendZOffset(array: ZOffsetVertexArray, numVertices: number, value: number) {
    const start = array.length;
    array.resize(start + numVertices);
    array.float32.fill(value, start, start + numVertices);
}

export class SymbolBuffers {
    layoutVertexArray: SymbolLayoutArray;
    layoutVertexBuffer!: VertexBuffer;

    indexArray: TriangleIndexArray;
    indexBuffer!: IndexBuffer;

    programConfigurations: ProgramConfigurationSet<SymbolStyleLayer>;
    segments: SegmentVector;

    dynamicLayoutVertexArray: SymbolDynamicLayoutArray;
    dynamicLayoutVertexBuffer!: VertexBuffer;

    opacityVertexArray: SymbolOpacityArray;
    opacityVertexBuffer!: VertexBuffer;

    // Global placement's fade state
    fadeVertexArray: SymbolFadeArray;
    fadeVertexBuffer: VertexBuffer | null | undefined;

    zOffsetVertexArray: ZOffsetVertexArray;
    zOffsetVertexBuffer!: VertexBuffer;

    orientationVertexArray: SymbolOrientationArray;
    orientationVertexBuffer!: VertexBuffer;

    iconTransitioningVertexArray: SymbolIconTransitioningArray;
    iconTransitioningVertexBuffer: VertexBuffer | null | undefined;

    globeExtVertexArray: SymbolGlobeExtArray;
    globeExtVertexBuffer: VertexBuffer | null | undefined;

    placedSymbolArray: PlacedSymbolArray;

    symbolInstanceIndices: number[];

    uboBinder: SymbolPropertyBinderUBO | null;
    featureIdArray: StructArrayLayout1f4;
    featureIdBuffer: VertexBuffer | null;

    cachedBatchIndices: number[] | null;
    cachedBatchSegments: Map<number, SegmentVector> | null;

    constructor(programConfigurations: ProgramConfigurationSet<SymbolStyleLayer>) {
        this.layoutVertexArray = new SymbolLayoutArray();
        this.indexArray = new TriangleIndexArray();
        this.programConfigurations = programConfigurations;
        this.segments = new SegmentVector();
        this.dynamicLayoutVertexArray = new SymbolDynamicLayoutArray();
        this.opacityVertexArray = new SymbolOpacityArray();
        this.fadeVertexArray = new SymbolFadeArray();
        this.placedSymbolArray = new PlacedSymbolArray();
        this.iconTransitioningVertexArray = new SymbolIconTransitioningArray();
        this.globeExtVertexArray = new SymbolGlobeExtArray();
        this.zOffsetVertexArray = new ZOffsetVertexArray();
        this.orientationVertexArray = new SymbolOrientationArray();
        this.symbolInstanceIndices = [];
        this.uboBinder = null;
        this.featureIdArray = new StructArrayLayout1f4();
        this.featureIdBuffer = null;
        this.cachedBatchIndices = null;
        this.cachedBatchSegments = null;
    }

    isEmpty(): boolean {
        return this.layoutVertexArray.length === 0 &&
            this.indexArray.length === 0 &&
            this.dynamicLayoutVertexArray.length === 0 &&
            this.opacityVertexArray.length === 0 &&
            this.iconTransitioningVertexArray.length === 0;
    }

    /**
     * Get cached batch grouping for UBO rendering.
     * Computes and caches the grouping on first call, then reuses cached result.
     *
     * Note: Takes a SegmentVector parameter to detect if we're rendering all segments
     * or just a subset (e.g., in sort-key path where segments are rendered individually).
     * Cache is only used when rendering all segments.
     */
    getBatchGrouping(segments: SegmentVector): {batchIndices: number[]; batchSegments: Map<number, SegmentVector>} {
        // Detect if we're rendering individual segments (sort-key path) vs all segments
        // In sort-key path, each segment is wrapped in new SegmentVector([segment])
        const isRenderingAllSegments = segments.get().length === this.segments.get().length;

        if (isRenderingAllSegments && this.cachedBatchIndices && this.cachedBatchSegments) {
            return {
                batchIndices: this.cachedBatchIndices,
                batchSegments: this.cachedBatchSegments
            };
        }

        // Compute batch grouping from the provided segments (not this.segments)
        // This ensures we respect sort-key reordering when present
        const segmentsByBatch = new Map<number, Array<typeof segments.segments[0]>>();
        for (const segment of segments.get()) {
            const batchIndex = segment.batchIndex !== undefined ? segment.batchIndex : 0;
            if (!segmentsByBatch.has(batchIndex)) {
                segmentsByBatch.set(batchIndex, []);
            }
            segmentsByBatch.get(batchIndex).push(segment);
        }

        // Sort batch indices and create SegmentVectors
        const batchIndices = Array.from(segmentsByBatch.keys()).sort((a, b) => a - b);
        const batchSegments = new Map<number, SegmentVector>();
        for (const batchIndex of batchIndices) {
            const segs = segmentsByBatch.get(batchIndex);
            batchSegments.set(batchIndex, new SegmentVector(segs));
        }

        // Only cache when rendering all segments
        if (isRenderingAllSegments) {
            this.cachedBatchIndices = batchIndices;
            this.cachedBatchSegments = batchSegments;
        }

        return {batchIndices, batchSegments};
    }

    // Returns a copy of [numVertices] vertices starting at [offset] as a Uint16Array snapshot.
    // Use restoreSymbolVertexData to write it back.
    snapshotSymbolVertexData(offset: number, numVertices: number): Uint16Array {
        assert(offset >= 0 && offset < this.layoutVertexArray.length, 'Invalid vertex offset');
        const start = offset * SYMBOL_VERTEX_STRIDE;
        return this.layoutVertexArray.uint16.slice(start, start + numVertices * SYMBOL_VERTEX_STRIDE);
    }

    // Writes a snapshot (produced by snapshotSymbolVertexData) back into the vertex array at [offset].
    restoreSymbolVertexData(offset: number, snapshot: Uint16Array) {
        assert(
            offset >= 0 && offset * SYMBOL_VERTEX_STRIDE + snapshot.length <= this.layoutVertexArray.uint16.length,
            'Invalid restore offset'
        );
        this.layoutVertexArray.uint16.set(snapshot, offset * SYMBOL_VERTEX_STRIDE);
    }

    snapshotIconTransitioningVertexData(offset: number, numVertices: number): Uint16Array {
        const start = offset * ICON_TRANSITIONING_STRIDE;
        return this.iconTransitioningVertexArray.uint16.slice(start, start + numVertices * ICON_TRANSITIONING_STRIDE);
    }

    restoreIconTransitioningVertexData(offset: number, snapshot: Uint16Array) {
        this.iconTransitioningVertexArray.uint16.set(snapshot, offset * ICON_TRANSITIONING_STRIDE);
    }

    updateSymbolVertexData(vertexIndex: number, anchorX: number, anchorY: number, newOx: number, newOy: number, newTx: number, newTy: number, newSizeX: number, newSizeY: number, pixelOffsetX: number, pixelOffsetY: number, minFontScaleX: number, minFontScaleY: number) {
        assert(vertexIndex >= 0 && vertexIndex < this.layoutVertexArray.length, 'Invalid vertex start index');

        const uint16Array = this.layoutVertexArray.uint16;
        const baseOffset = vertexIndex * SYMBOL_VERTEX_STRIDE;
        uint16Array[baseOffset] = anchorX;
        uint16Array[baseOffset + 1] = anchorY;
        uint16Array[baseOffset + 2] = newOx;
        uint16Array[baseOffset + 3] = newOy;
        uint16Array[baseOffset + 4] = newTx;
        uint16Array[baseOffset + 5] = newTy;
        uint16Array[baseOffset + 6] = newSizeX;
        uint16Array[baseOffset + 7] = newSizeY;
        uint16Array[baseOffset + 8] = pixelOffsetX;
        uint16Array[baseOffset + 9] = pixelOffsetY;
        uint16Array[baseOffset + 10] = minFontScaleX;
        uint16Array[baseOffset + 11] = minFontScaleY;
    }

    upload(context: Context, dynamicIndexBuffer: boolean, upload?: boolean, update?: boolean, createZOffsetBuffer?: boolean, hasAppearances?: boolean) {
        if (this.isEmpty()) {
            return;
        }

        if (upload) {
            // Invalidate batch grouping cache: index buffer is being replaced, so
            // any cached segment→batch mapping is stale.
            this.cachedBatchIndices = null;
            this.cachedBatchSegments = null;

            this.layoutVertexBuffer = context.createVertexBuffer(this.layoutVertexArray, symbolLayoutAttributes.members, !!hasAppearances);
            this.indexBuffer = context.createIndexBuffer(this.indexArray, dynamicIndexBuffer);
            this.dynamicLayoutVertexBuffer = context.createVertexBuffer(this.dynamicLayoutVertexArray, dynamicLayoutAttributes.members, true);
            this.opacityVertexBuffer = context.createVertexBuffer(this.opacityVertexArray, shaderOpacityAttributes, true);
            if (this.iconTransitioningVertexArray.length > 0) {
                this.iconTransitioningVertexBuffer = context.createVertexBuffer(this.iconTransitioningVertexArray, iconTransitioningAttributes.members, true);
            }
            if (this.fadeVertexArray.length > 0) {
                this.fadeVertexBuffer = context.createVertexBuffer(this.fadeVertexArray, placementFadeAttributes.members, true);
            }
            if (this.globeExtVertexArray.length > 0) {
                this.globeExtVertexBuffer = context.createVertexBuffer(this.globeExtVertexArray, symbolGlobeExtAttributes.members, true);
            }
            if (!this.zOffsetVertexBuffer && (this.zOffsetVertexArray.length > 0 || !!createZOffsetBuffer)) {
                this.zOffsetVertexBuffer = context.createVertexBuffer(this.zOffsetVertexArray, zOffsetAttributes.members, true);
                // later updateZOffset rebuilds go through a shared scratch, so free the bucket's copy
                this.zOffsetVertexArray.clear();
                this.zOffsetVertexArray._trim();
            }
            if (!this.orientationVertexBuffer && this.orientationVertexArray && this.orientationVertexArray.length > 0) {
                this.orientationVertexBuffer = context.createVertexBuffer(this.orientationVertexArray, orientationAttributes.members, true);
                assert(this.orientationVertexBuffer.length === this.layoutVertexBuffer.length);
            }
            // This is a performance hack so that we can write to opacityVertexArray with uint32s
            // even though the shaders read uint8s
            this.opacityVertexBuffer.itemSize = 1;

            if (this.featureIdArray.length > 0) {
                this.featureIdBuffer = context.createVertexBuffer(this.featureIdArray, featureIdAttributes.members, false);
            }
        }
        if (upload || update) {
            this.programConfigurations.upload(context);

            if (this.uboBinder) {
                this.uboBinder.upload(context);
            }
        }
    }

    destroy() {
        if (!this.layoutVertexBuffer) return;

        this.layoutVertexBuffer.destroy();
        this.indexBuffer.destroy();
        this.programConfigurations.destroy();
        this.segments.destroy();
        this.dynamicLayoutVertexBuffer.destroy();
        this.opacityVertexBuffer.destroy();
        if (this.iconTransitioningVertexBuffer) {
            this.iconTransitioningVertexBuffer.destroy();
        }
        if (this.fadeVertexBuffer) {
            this.fadeVertexBuffer.destroy();
        }
        if (this.globeExtVertexBuffer) {
            this.globeExtVertexBuffer.destroy();
        }
        if (this.zOffsetVertexBuffer) {
            this.zOffsetVertexBuffer.destroy();
        }
        if (this.orientationVertexBuffer) {
            this.orientationVertexBuffer.destroy();
        }

        if (this.featureIdBuffer) {
            this.featureIdBuffer.destroy();
        }
        if (this.uboBinder) {
            this.uboBinder.destroy();
        }
    }
}

register(SymbolBuffers, 'SymbolBuffers', {omit: ['cachedBatchIndices', 'cachedBatchSegments']});

class CollisionBuffers {
    layoutVertexArray: StructArray;
    layoutAttributes: Array<StructArrayMember>;
    layoutVertexBuffer!: VertexBuffer;

    indexArray: TriangleIndexArray | LineIndexArray;
    indexBuffer!: IndexBuffer;

    segments: SegmentVector;

    collisionVertexArray: CollisionVertexArray;
    collisionVertexBuffer!: VertexBuffer;

    collisionVertexArrayExt: CollisionVertexExtArray;
    collisionVertexBufferExt!: VertexBuffer;

    constructor(
        LayoutArray: Class<StructArray>,
        layoutAttributes: Array<StructArrayMember>,
        IndexArray: Class<TriangleIndexArray | LineIndexArray>
    ) {
        this.layoutVertexArray = new LayoutArray();
        this.layoutAttributes = layoutAttributes;
        this.indexArray = new IndexArray();
        this.segments = new SegmentVector();
        this.collisionVertexArray = new CollisionVertexArray();
        this.collisionVertexArrayExt = new CollisionVertexExtArray();
    }

    upload(context: Context) {
        this.layoutVertexBuffer = context.createVertexBuffer(this.layoutVertexArray, this.layoutAttributes);
        this.indexBuffer = context.createIndexBuffer(this.indexArray);
        this.collisionVertexBuffer = context.createVertexBuffer(this.collisionVertexArray, collisionVertexAttributes.members, true);
        this.collisionVertexBufferExt = context.createVertexBuffer(this.collisionVertexArrayExt, collisionVertexAttributesExt.members, true);
    }

    destroy() {
        if (!this.layoutVertexBuffer) return;
        this.layoutVertexBuffer.destroy();
        this.indexBuffer.destroy();
        this.segments.destroy();
        this.collisionVertexBuffer.destroy();
        this.collisionVertexBufferExt.destroy();
    }
}

register(CollisionBuffers, 'CollisionBuffers');

/**
 * Unlike other buckets, which simply implement #addFeature with type-specific
 * logic for (essentially) triangulating feature geometries, SymbolBucket
 * requires specialized behavior:
 *
 * 1. WorkerTile#parse(), the logical owner of the bucket creation process,
 *    calls SymbolBucket#populate(), which resolves text and icon tokens on
 *    each feature, adds each glyphs and symbols needed to the passed-in
 *    collections options.glyphDependencies and options.iconDependencies, and
 *    stores the feature data for use in subsequent step (this.features).
 *
 * 2. WorkerTile asynchronously requests from the main thread all of the glyphs
 *    and icons needed (by this bucket and any others). When glyphs and icons
 *    have been received, the WorkerTile creates a CollisionIndex and invokes:
 *
 * 3. performSymbolLayout(bucket, stacks, icons) perform texts shaping and
 *    layout on a Symbol Bucket. This step populates:
 *      `this.symbolInstances`: metadata on generated symbols
 *      `collisionBoxArray`: collision data for use by foreground
 *      `this.text`: SymbolBuffers for text symbols
 *      `this.icons`: SymbolBuffers for icons
 *      `this.iconCollisionBox`: Debug SymbolBuffers for icon collision boxes
 *      `this.textCollisionBox`: Debug SymbolBuffers for text collision boxes
 *    The results are sent to the foreground for rendering
 *
 * 4. Placement.updateBucketOpacities() is run on the foreground,
 *    and uses the CollisionIndex along with current camera settings to determine
 *    which symbols can actually show on the map. Collided symbols are hidden
 *    using a dynamic "OpacityVertexArray".
 *
 * @private
 */
class SymbolBucket implements Bucket, SymbolSource {
    static addDynamicAttributes: typeof addDynamicAttributes;

    collisionBoxArray: CollisionBoxArray;
    zoom: number;
    lut: LUT | null;
    overscaling: number;
    layers: Array<SymbolStyleLayer>;
    layerIds: Array<string>;
    stateDependentLayers!: Array<SymbolStyleLayer>;
    stateDependentLayerIds: Array<string>;

    index: number;
    sdfIcons!: boolean;
    iconsInText!: boolean;
    iconsNeedLinear!: boolean;
    bucketInstanceId!: number;
    justReloaded!: boolean;
    hasPattern: boolean;
    fullyClipped: boolean;

    textSizeData: SizeData;
    iconSizeData: SizeData;

    glyphOffsetArray!: GlyphOffsetArray;
    lineVertexArray!: SymbolLineVertexArray;
    features!: Array<SymbolFeature>;
    featureAppearanceData: Map<number, AppearanceFeatureData>;
    symbolInstances!: SymbolInstanceArray;
    hasAnySecondaryIcon: boolean;
    sortKeyRanges: Array<SortKeyRange>;
    // New placement pipeline (see addToPlacement). Start of this bucket's generated symbolId range;
    // assigned on the first placement run and then stable. The variant for symbolInstances[i] uses
    // symbolId (placementIdRangeStart + i).
    placementIdRangeStart: number | null;
    // Current per-instance visibility, fed back into placement next run as
    // GlobalPlacementPriority.symbolVariantVisibility so it can detect visible<->hidden transitions
    // and apply the right collision hysteresis. Parallel to symbolInstances; allocated (all hidden)
    // alongside placementIdRangeStart.
    placementVariantVisible: Array<boolean>;
    placementFadeRefTime: Float64Array;
    placementFadeRunning: Uint8Array;
    _activeFadeCount: number;
    _fadeDuration: number;
    pixelRatio: number;
    tilePixelRatio!: number;
    compareText!: {
        [_: string]: Array<Point>;
    };
    fadeStartTime: number;
    sortFeaturesByKey: boolean;
    sortFeaturesByY: boolean;
    canOverlap: boolean;
    sortedAngle!: number;
    featureSortOrder!: Array<number>;

    collisionCircleArray: Array<number>;
    placementInvProjMatrix: mat4;
    placementViewportMatrix: mat4;

    text!: SymbolBuffers;
    icon!: SymbolBuffers;
    textCollisionBox!: CollisionBuffers;
    iconCollisionBox!: CollisionBuffers;
    uploaded!: boolean;
    sourceLayerIndex: number;
    sourceID: string;
    symbolInstanceIndexes!: Array<number>;
    writingModes: Array<number>;
    allowVerticalPlacement!: boolean;
    hasRTLText: boolean;
    projection: ProjectionSpecification;
    projectionInstance: Projection | null | undefined;
    tileTransformInstance: TileTransform | null | undefined;
    hasAnyIconTextFit: boolean;
    hasAnyZOffset: boolean;
    symbolInstanceIndexesSortedZOffset!: Array<number>;
    zOffsetSortDirty: boolean;
    zOffsetBuffersNeedUpload: boolean;

    elevationType: ElevationType;
    hdExt?: SymbolHDExtension;

    activeReplacements: Array<Region>;
    replacementUpdateTime: number;

    worldview: string;
    localizable: boolean;
    maxUniformBlockSizeDwords: number | null | undefined;
    iconAtlasPositions: ImagePositionMap | undefined;
    hasAppearances: boolean | null;
    featureAppearances: FeatureAppearances | null;

    constructor(options: BucketParameters<SymbolStyleLayer>) {
        this.collisionBoxArray = options.collisionBoxArray;
        this.zoom = options.zoom;
        this.overscaling = options.overscaling;
        this.layers = options.layers;
        this.layerIds = this.layers.map(layer => layer.fqid);
        this.index = options.index;
        this.pixelRatio = options.pixelRatio;
        this.sourceLayerIndex = options.sourceLayerIndex;
        this.hasPattern = false;
        this.hasRTLText = false;
        this.fullyClipped = false;
        this.hasAnyIconTextFit = false;
        this.sortKeyRanges = [];
        this.placementIdRangeStart = null;
        this.placementVariantVisible = [];
        this.placementFadeRefTime = new Float64Array(0);
        this.placementFadeRunning = new Uint8Array(0);
        this._activeFadeCount = 0;
        this._fadeDuration = 0;

        this.collisionCircleArray = [];
        this.placementInvProjMatrix = mat4.identity([]);
        this.placementViewportMatrix = mat4.identity([]);

        const layer = this.layers[0];
        const unevaluatedLayoutValues = layer._unevaluatedLayout._values;

        this.worldview = options.worldview;
        this.localizable = options.localizable;
        this.maxUniformBlockSizeDwords = options.maxUniformBlockSizeDwords;

        this.textSizeData = getSizeData(this.zoom, unevaluatedLayoutValues['text-size'], this.worldview, options.availableImages);

        this.iconSizeData = getSizeData(this.zoom, unevaluatedLayoutValues['icon-size'], this.worldview, options.availableImages);

        const layout = this.layers[0].layout;
        const sortKey = layout.get('symbol-sort-key');
        const zOrder = layout.get('symbol-z-order');

        this.lut = options.lut;

        this.canOverlap =
            layout.get('text-allow-overlap') ||
            layout.get('icon-allow-overlap') ||
            layout.get('text-ignore-placement') ||
            layout.get('icon-ignore-placement');

        this.sortFeaturesByKey = zOrder !== 'viewport-y' && sortKey.constantOr(1) !== undefined;
        const zOrderByViewportY = zOrder === 'viewport-y' || (zOrder === 'auto' && !this.sortFeaturesByKey);
        this.sortFeaturesByY = zOrderByViewportY && this.canOverlap;

        this.writingModes = layout.get('text-writing-mode').map(wm => WritingMode[wm]);

        this.stateDependentLayerIds = this.layers.filter((l) => l.isStateDependent()).map((l) => l.id);

        this.sourceID = options.sourceID;
        this.projection = options.projection;
        this.hasAnyZOffset = false;
        this.zOffsetSortDirty = false;

        this.zOffsetBuffersNeedUpload = false;

        this.elevationType = 'none';

        this.activeReplacements = [];
        this.replacementUpdateTime = 0;
        this.hasAnySecondaryIcon = false;
        this.hasAppearances = null;
        this.featureAppearances = null;
        this.featureAppearanceData = new Map();
    }

    hasAnyAppearanceLayoutProperty(properties: keyof AppearanceProps | (keyof AppearanceProps)[]): boolean {
        const layer = this.layers[0];
        const appearances = layer.getAppearances();
        if (!appearances || appearances.length === 0) {
            return false;
        }

        const propertiesToCheck = Array.isArray(properties) ? properties : [properties];
        return appearances.some(appearance => {
            return propertiesToCheck.some(propertyName => appearance.hasLayoutProperty(propertyName));
        });
    }

    getAppearanceFeatureData(featureIndex: number): AppearanceFeatureData | undefined {
        return this.featureAppearanceData.get(featureIndex);
    }

    createArrays() {
        this.text = new SymbolBuffers(
            new ProgramConfigurationSet(this.layers, {zoom: this.zoom, lut: this.lut}, (property) => {
                return property.startsWith('text') || property.startsWith('symbol');
            })
        );
        this.icon = new SymbolBuffers(
            new ProgramConfigurationSet(this.layers, {zoom: this.zoom, lut: this.lut}, (property) => {
                return property.startsWith('icon') || property.startsWith('symbol');
            })
        );

        this.text.uboBinder = new SymbolPropertyBinderUBO(this.layers[0], this.zoom, this.lut, true, '', this.maxUniformBlockSizeDwords);
        this.icon.uboBinder = new SymbolPropertyBinderUBO(this.layers[0], this.zoom, this.lut, false, '', this.maxUniformBlockSizeDwords);

        this.glyphOffsetArray = new GlyphOffsetArray();
        this.lineVertexArray = new SymbolLineVertexArray();
        this.symbolInstances = new SymbolInstanceArray();
    }

    calculateGlyphDependencies(text: string, stack: Record<number, boolean>, textAlongLine: boolean, allowVerticalPlacement: boolean, doesAllowVerticalWritingMode: boolean) {
        for (const char of text) {
            const codePoint = char.codePointAt(0);
            if (codePoint === undefined) break;
            stack[codePoint] = true;
            if (allowVerticalPlacement && doesAllowVerticalWritingMode && codePoint <= 65535) {
                const verticalChar = verticalizedCharacterMap[char];
                if (verticalChar) {
                    stack[verticalChar.charCodeAt(0)] = true;
                }
            }
        }
    }

    calculateEffectiveAppearanceIconSize(
        activeAppearance: SymbolAppearance,
        currentZoom: number,
        evaluationFeature: EvaluationFeature,
        canonical: CanonicalTileID,
        availableImages: Array<ImageId>,
        iconScaleFactor: number,
        layoutIconSize: number
    ): number {
        // If appearance doesn't define icon-size, use layout value
        if (!activeAppearance.hasLayoutProperty('icon-size')) {
            return layoutIconSize * iconScaleFactor;
        }

        let effectiveIconSize = 1;
        const unevaluatedIconSize = activeAppearance.getUnevaluatedLayoutProperty('icon-size') as PropertyValue<number, PossiblyEvaluatedPropertyValue<number>>;
        const iconSizeData = getSizeData(this.zoom, unevaluatedIconSize, this.worldview, availableImages);
        const sizeValue = evaluateSizeForZoom(iconSizeData, currentZoom);
        if (iconSizeData.kind === 'constant' || iconSizeData.kind === 'camera') {
            effectiveIconSize = sizeValue.uSize;
        }
        if (iconSizeData.kind === 'composite') {
            const {minZoom, maxZoom} = iconSizeData;
            const possibleSizeMin = unevaluatedIconSize.possiblyEvaluate(new EvaluationParameters(minZoom, {worldview: this.worldview}), canonical);
            const possibleSizeMax = unevaluatedIconSize.possiblyEvaluate(new EvaluationParameters(maxZoom, {worldview: this.worldview}), canonical);
            const sizeMin = possibleSizeMin.evaluate(evaluationFeature, {}, canonical, availableImages);
            const sizeMax = possibleSizeMax.evaluate(evaluationFeature, {}, canonical, availableImages);
            effectiveIconSize = sizeMin + (sizeMax - sizeMin) * sizeValue.uSizeT;
        }
        if (iconSizeData.kind === 'source') {
            const possibleSize = unevaluatedIconSize.possiblyEvaluate(new EvaluationParameters(this.zoom, {worldview: this.worldview}), canonical);
            effectiveIconSize = possibleSize.evaluate(evaluationFeature, {}, canonical, availableImages);
        }

        return effectiveIconSize * iconScaleFactor;
    }

    updateFootprints(_id: UnwrappedTileID, _footprints: Array<TileFootprint>) {
    }

    // placement::SymbolSource visibility callbacks, invoked by GlobalPlacement.finishPlacementRun()
    // only when a variant's visibility changes. placementRunTimestamp anchors the new fade ramp --
    // see _setSymbolVariantVisibility.
    hideSymbolVariant(variantId: SymbolVariantId, placementRunTimestamp: number): void {
        this._setSymbolVariantVisibility(variantId, false, placementRunTimestamp);
    }

    showSymbolVariant(variantId: SymbolVariantId, placementRunTimestamp: number): void {
        this._setSymbolVariantVisibility(variantId, true, placementRunTimestamp);
    }

    // Fills the (until-now-empty) fade buffers with one settled-hidden entry per vertex, matching
    // the layout vertex buffers' length. Unlike the legacy pipeline, new placement never runs
    // Placement#updateBucketOpacities to build these from scratch each run, so this bucket has to
    // seed them itself, once, before the first upload.
    _initPlacementOpacities(): void {
        const fillHidden = (buffer: SymbolBuffers) => {
            const vertexCount = buffer.layoutVertexArray.length;
            for (let i = 0; i < vertexCount; i++) buffer.fadeVertexArray.emplaceBack(0, FADE_SETTLED_HIDDEN);
        };
        if (this.hasTextData()) fillHidden(this.text);
        if (this.hasIconData()) fillHidden(this.icon);
    }

    // Sets the variant's instance to fade toward visible or hidden, recovering the instance index
    // arithmetically from the generated id. Mirrors the geometry fed in addToPlacement:
    // the default text placement and the icon.
    _setSymbolVariantVisibility(variantId: SymbolVariantId, visible: boolean, now: number): void {
        assert(this.placementIdRangeStart !== null);
        const index = variantId.symbolId.symbolId - this.placementIdRangeStart;
        const instance = this.symbolInstances.get(index);

        const settled = this.placementFadeRunning[index] === 0;
        const currentOpacity = evaluateFadeOpacity(this.placementFadeRefTime[index], this.placementVariantVisible[index], settled, now, this._fadeDuration);
        const refTime = visible ? now - currentOpacity * this._fadeDuration : now - (1 - currentOpacity) * this._fadeDuration;

        if (this.placementFadeRunning[index] === 0) this._activeFadeCount++;

        this.placementVariantVisible[index] = visible;
        this.placementFadeRefTime[index] = refTime;
        this.placementFadeRunning[index] = 1;

        this._writeFadeState(index, instance, refTime, fadeState(visible, false));
    }

    hasActiveFade(): boolean {
        return this._activeFadeCount > 0;
    }

    // A placed symbol owns a contiguous run of vertices from its vertexStartIndex (4 per glyph
    // quad, one fade entry per vertex); write the same (refTime, state) into all of them.
    _writeFadeState(index: number, instance: SymbolInstance, refTime: number, state: number): void {
        const write = (buffer: SymbolBuffers, placedIndex: number) => {
            if (placedIndex < 0) return;
            const placed = buffer.placedSymbolArray.get(placedIndex);
            const vertexStart = placed.vertexStartIndex;
            for (let i = 0; i < placed.numGlyphs * 4; i++) buffer.fadeVertexArray.emplace(vertexStart + i, refTime, state);
            if (buffer.fadeVertexBuffer) buffer.fadeVertexBuffer.updateData(buffer.fadeVertexArray);
        };

        if (this.hasTextData()) write(this.text, this.defaultPlacedTextSymbolIndex(instance, index));
        if (this.hasIconData()) write(this.icon, instance.placedIconSymbolIndex);
    }

    _settleFinishedFade(index: number, instance: SymbolInstance, now: number): void {
        if (this.placementFadeRunning[index] === 0) return;
        if (now - this.placementFadeRefTime[index] < this._fadeDuration) return;
        this.placementFadeRunning[index] = 0;
        this._activeFadeCount--;
        this._writeFadeState(index, instance, 0, fadeState(this.placementVariantVisible[index], true));
    }

    // Drops what new placement decided about this bucket's symbols, hiding all of them. Called when
    // the tile has been out of the render set (e.g. returning from the tile cache), so placement has
    // not seen it and its decisions have gone stale -- keeping them would let a returning tile
    // outrank the symbols that are actually on screen, since a visible symbol is placed first.
    resetPlacementVisibility(): void {
        if (this.placementIdRangeStart === null) {
            // Never fed to new placement (legacy placement, or not placed yet), so there is no state to drop.
            return;
        }

        this.placementVariantVisible.fill(false);
        this.placementFadeRefTime.fill(0);
        this.placementFadeRunning.fill(0);
        this._activeFadeCount = 0;

        const hideAll = (buffer: SymbolBuffers) => {
            for (let i = 0; i < buffer.fadeVertexArray.length; i++) buffer.fadeVertexArray.emplace(i, 0, FADE_SETTLED_HIDDEN);
            if (buffer.fadeVertexBuffer) buffer.fadeVertexBuffer.updateData(buffer.fadeVertexArray);
        };
        if (this.hasTextData()) hideAll(this.text);
        if (this.hasIconData()) hideAll(this.icon);
    }

    // New placement pipeline: walks symbolInstances and registers one placement variant per symbol,
    // streaming its icon and/or text collision box(es) into the run. Only the minimal point-symbol
    // case is handled -- icon-text-fit, appearances and along-line placement are skipped (with a
    // one-time warning), and symbols with neither an icon nor a text collision box are skipped
    // silently. `textPixelRatio` (tile.tileSize / EXTENT) converts tile-space offsets to CSS pixels,
    // matching the legacy collision index formula (see CollisionIndex#placeCollisionBox).
    addToPlacement(globalPlacement: GlobalPlacement, idRangeAllocator: SymbolIdRangeAllocator, layerUid: number, posMatrix: mat4, invMatrix: mat4, mercatorCenter: [number, number], transform: Transform, textPixelRatio: number, tile: Tile, fogState: FogState | null | undefined, groupOrders: PlacementGroupOrders, styleLayerOrder: number, featureStates: FeatureStates, replacementSource: ReplacementSource | null, fadeDuration: number, childCoverageRects: ReadonlyArray<TileCoverageRect>): void {
        if (this.symbolInstances.length === 0) return;

        this._fadeDuration = fadeDuration;

        if (replacementSource) {
            this.updateReplacement(tile.tileID, replacementSource);
        }

        if (!tile.collisionBoxArray) return;

        if (this.placementIdRangeStart === null) {
            this.placementIdRangeStart = idRangeAllocator.allocateRange(layerUid, this.symbolInstances.length);
            this.placementVariantVisible = new Array<boolean>(this.symbolInstances.length).fill(false);
            this.placementFadeRefTime = new Float64Array(this.symbolInstances.length);
            this.placementFadeRunning = new Uint8Array(this.symbolInstances.length);
            this._initPlacementOpacities();
        }
        const rangeStart = this.placementIdRangeStart;
        const placementNow = globalPlacement.timestamp();

        const layer = this.layers[0];
        if (layer.appearances && layer.appearances.length > 0) {
            warnOnce('new placement: symbol appearances are not supported yet; hiding symbols');
            return;
        }
        if (layer.layout.get('symbol-placement') !== 'point') {
            warnOnce('new placement: along-line symbol placement is not supported yet; hiding symbols');
            return;
        }
        const hasVariableTextAnchor = !!layer.layout.get('text-variable-anchor');

        const bucketProjection = this.getProjection();
        const globeToMercator = bucketProjection.name === 'globe' ? globeToMercatorTransition(transform.zoom) : 1;
        const isGlobeToMercatorTransition = globeToMercator < 1;

        const unwrappedTileID = tile.tileID.toUnwrapped();
        const tileIdentity: TileIdentity = {overscaledZ: tile.tileID.overscaledZ, wrap: tile.tileID.wrap, canonical: tile.tileID.canonical};
        const pitched = transform.pitch > 0;
        // Minimal first version: default collision rules (collide against, and insert into, the
        // collision grid). Priority is built per instance below so its current visibility feeds back
        // into placement.
        const placementRules = defaultPlacementRules();

        // Size is evaluated once per run for the current zoom (mirroring the shader's per-frame size
        // interpolation); the per-feature value is read below via getSymbolInstance{Icon,Text}Size.
        const zoom = transform.zoom;
        const iconZoomSize = evaluateSizeForZoom(this.iconSizeData, zoom);
        const textZoomSize = evaluateSizeForZoom(this.textSizeData, zoom);

        // `symbol-elevation-reference` only decides what a box's z offset is measured from: `sea` from
        // sea level, `ground`/`hd-road-markup` from the terrain under the anchor.
        // The offset itself is `symbol-z-offset` plus `instance.zOffset`, the road/building height baked
        // in ahead of this run
        const symbolZOffsetProperty = layer.paint.get('symbol-z-offset');
        const elevationFromSea = layer.layout.get('symbol-elevation-reference') === 'sea';
        const needsFeatureForZOffset = !symbolZOffsetProperty.isConstant();
        const constantSymbolZOffset: number = needsFeatureForZOffset ? 0 : symbolZOffsetProperty.evaluate(null, {});
        const terrainElevation = transform.elevation;

        // This layer's implicit group for symbols naming no group or an invalid one
        const implicitGroupOrder = subgroupOrderForLayerPosition(styleLayerOrder);
        const resolveGroupOrder = (groupName: string | null | undefined): number => {
            if (!groupName) return implicitGroupOrder;
            const order = groupOrders.get(makeFQID(groupName, layer.scope));
            if (order !== undefined) return order;
            warnOnce(`global placement: placement-group "${groupName}" does not match any placement-group layer; using the layer's implicit group`);
            return implicitGroupOrder;
        };

        const placementPriorityProperty = layer.paint.get('placement-priority');
        const placementGroupProperty = layer.paint.get('placement-group');
        const needsFeatureForPlacementPriority = !placementPriorityProperty.isConstant();
        const needsFeatureForPlacementGroup = !placementGroupProperty.isConstant();
        const placementPriorityFallback: number = needsFeatureForPlacementPriority ? 0 : placementPriorityProperty.evaluate(null, {});
        const placementSubgroupOrderFallback: number = needsFeatureForPlacementGroup ? implicitGroupOrder : resolveGroupOrder(placementGroupProperty.evaluate(null, {}));
        const needsFeatureForPlacementState = needsFeatureForPlacementPriority || needsFeatureForPlacementGroup;
        const collectDebugData = globalPlacement.isCollectingDebugData();
        const needsFeature = needsFeatureForZOffset || needsFeatureForPlacementState || collectDebugData;

        const latestFeatureIndex = needsFeature ? tile.latestFeatureIndex : null;
        if (latestFeatureIndex) latestFeatureIndex.loadVTLayers();
        const sourceLayerName = latestFeatureIndex ? latestFeatureIndex.sourceLayerCoder.decode(this.sourceLayerIndex) : '';

        // Raises a box's anchor to the height the symbol is drawn at, blends it
        // toward its mercator-tile position during the globe-to-mercator transition, then projects it
        // into logical viewport pixels. Inlined into one function -- rather than three chained ones --
        // since this runs per collision box in the per-instance hot loop below and each intermediate
        // step previously allocated its own throwaway {x, y, z} object.
        const projectCollisionBoxAnchor = (box: SingleCollisionBox, instance: SymbolInstance, symbolZOffsetValue: number): ProjectedAnchor => {
            let boxElevation: number;
            if (this.elevationType === 'road') {
                boxElevation = elevationFromSea ? symbolZOffsetValue :
                    symbolZOffsetValue + Elevation.getAtTileOffset(tile.tileID, new Point(box.tileAnchorX, box.tileAnchorY), terrainElevation, null);
            } else {
                const elevationFeature = this.hdExt ? this.hdExt.elevationFeatures[instance.elevationFeatureIndex] : undefined;
                boxElevation = elevationFromSea ? symbolZOffsetValue :
                    symbolZOffsetValue + Elevation.getAtTileOffset(tile.tileID, new Point(box.tileAnchorX, box.tileAnchorY), terrainElevation, elevationFeature);
            }
            boxElevation += instance.zOffset;

            let x = box.projectedAnchorX;
            let y = box.projectedAnchorY;
            let z = box.projectedAnchorZ;
            const elevated = !!boxElevation;
            if (elevated) {
                const [ux, uy, uz] = bucketProjection.upVector(tile.tileID.canonical, box.tileAnchorX, box.tileAnchorY);
                const upScale = bucketProjection.upVectorScale(tile.tileID.canonical, transform.center.lat, transform.worldSize).metersToTile * boxElevation;
                x += ux * upScale;
                y += uy * upScale;
                z += uz * upScale;
            }

            if (bucketProjection.name === 'globe' && isGlobeToMercatorTransition) {
                const canonical = tile.tileID.canonical;
                const tilesCount = 1 << canonical.z;
                let mercatorX = (box.tileAnchorX / EXTENT + canonical.x) / tilesCount - mercatorCenter[0];
                const mercatorY = ((box.tileAnchorY / EXTENT + canonical.y) / tilesCount - mercatorCenter[1]) * EXTENT;
                mercatorX = wrap(mercatorX, -0.5, 0.5) * EXTENT;
                const mercatorPosition = vec4.fromValues(mercatorX, mercatorY, EXTENT / (2.0 * Math.PI), 1.0);
                vec4.transformMat4(mercatorPosition, mercatorPosition, invMatrix);
                x = mix(x, mercatorPosition[0], globeToMercator);
                y = mix(y, mercatorPosition[1], globeToMercator);
                z = mix(z, mercatorPosition[2], globeToMercator);
            }

            const checkOcclusion = pitched || elevated || bucketProjection.name === 'globe';
            return projectAnchor(posMatrix, x, y, z, transform, bucketProjection, checkOcclusion, fogState, unwrappedTileID);
        };

        // Streams a collision box into the run as a screen-aligned viewport rectangle. The box is
        // stored anchor-relative: `box.x1/y1/x2/y2` are unscaled offsets from
        // `box.projectedAnchor{X,Y,Z}` in the feature's own layout units (shaping already baked the
        // icon/text extent and *-offset/*-rotate into them). `scale` takes the offsets to the
        // symbol's on-screen size at the current zoom. Absent, occluded and degenerate boxes
        // contribute nothing.
        // `getScale` is called only once `box` is known to exist: a symbol with no icon (or no text)
        // has no valid placedIconSymbolIndex/placedSymbolArray entry to evaluate a size from.
        const addCollisionBox = (box: SingleCollisionBox | undefined, instance: SymbolInstance, symbolZOffsetValue: number, getScale: () => number) => {
            if (!box) return;
            const anchor = projectCollisionBoxAnchor(box, instance, symbolZOffsetValue);
            // Occluded, too far away to be worth a place or behind the camera
            if (anchor.occluded || anchor.perspectiveRatio <= MIN_COLLISION_PERSPECTIVE_RATIO) return;
            const scale = getScale();
            const tileToViewport = textPixelRatio * anchor.perspectiveRatio;
            const left = (box.x1 * scale - box.padding) * tileToViewport + anchor.x;
            const top = (box.y1 * scale - box.padding) * tileToViewport + anchor.y;
            const right = (box.x2 * scale + box.padding) * tileToViewport + anchor.x;
            const bottom = (box.y2 * scale + box.padding) * tileToViewport + anchor.y;
            if (!(left < right) || !(top < bottom)) return;
            globalPlacement.addGeometry({kind: 'box', left, top, right, bottom});
        };

        for (let index = 0; index < this.symbolInstances.length; index++) {
            const instance = this.symbolInstances.get(index);

            if (instance.hasIconTextFit) {
                warnOnce('new placement: icon-text-fit is not supported yet; skipping symbols');
                continue;
            }
            const hasText = instance.numHorizontalGlyphVertices > 0 || instance.numVerticalGlyphVertices > 0;
            if (hasVariableTextAnchor && hasText) {
                warnOnce('new placement: text-variable-anchor is not supported yet; skipping symbols');
                continue;
            }
            if (instance.numVerticalGlyphVertices > 0) {
                warnOnce('new placement: vertical text-writing-mode is not supported yet; skipping symbols');
                continue;
            }

            const collisionArrays = this.getCollisionArrays(tile.collisionBoxArray, instance);
            if (!collisionArrays.iconBox && !collisionArrays.textBox) {
                // Neither icon nor text: the symbol contributes no collision geometry, so never begin a variant.
                continue;
            }

            // Hides a symbol whose anchor falls under a 3D building/landmark/clip-layer footprint,
            // mirroring legacy Placement#updateBucketOpacities
            // Unlike the old algorithm, a clipped instance feeds no geometry at all, so its
            // collision-grid slot is freed for other symbols
            let clipped = false;
            for (const region of this.activeReplacements) {
                if (skipClipping(region, styleLayerOrder, LayerTypeMask.Symbol, layer.scope)) continue;
                if (region.min.x > instance.tileAnchorX || instance.tileAnchorX > region.max.x ||
                    region.min.y > instance.tileAnchorY || instance.tileAnchorY > region.max.y) continue;
                const p = transformPointToTile(instance.tileAnchorX, instance.tileAnchorY, tile.tileID.canonical, region.footprintTileId.canonical);
                clipped = pointInFootprint(p, region.footprint);
                if (clipped) break;
            }

            const symbolId: SymbolId = {styleLayerId: layerUid, symbolIdOrigin: SymbolIdOrigin.GENERATED, symbolId: rangeStart + index};
            const variantId: SymbolVariantId = {symbolId, variantIdx: 0};

            // This tile may be a coarser tile retained alongside an already-loaded finer child tile
            // (e.g. while zooming out). Without a stable id, we can't dedupe the same feature placed
            // from both tiles, so we conservatively hide all symbols from a parent tile wherever a child
            // tile covers it
            const shadowedByChildTile = childCoverageRects.length > 0 && !hasStableId(symbolId) &&
                childCoverageRects.some((r) => instance.tileAnchorX >= r.min.x && instance.tileAnchorX < r.max.x &&
                    instance.tileAnchorY >= r.min.y && instance.tileAnchorY < r.max.y);

            const feature = latestFeatureIndex ? latestFeatureIndex.loadFeature({
                featureIndex: instance.featureIndex,
                sourceLayerIndex: this.sourceLayerIndex,
                bucketIndex: this.index,
                layoutVertexArrayOffset: 0,
            }) : null;

            const featureId = (needsFeatureForPlacementState || collectDebugData) && feature && latestFeatureIndex ? latestFeatureIndex.getId(feature, sourceLayerName) : undefined;
            const featureState = (featureId !== undefined && featureStates[String(featureId)]) || EMPTY_FEATURE_STATE;
            const symbolPlacementPriority = needsFeatureForPlacementPriority && feature ? placementPriorityProperty.evaluate(feature, featureState) : placementPriorityFallback;
            const placementSubgroupOrder = needsFeatureForPlacementGroup && feature ? resolveGroupOrder(placementGroupProperty.evaluate(feature, featureState)) : placementSubgroupOrderFallback;

            this._settleFinishedFade(index, instance, placementNow);

            // Feed the instance's current visibility so placement can detect show/hide transitions
            // and pick the matching collision hysteresis. Placement is Fixed: along-line symbols are
            // skipped above, so every fed symbol has a fixed position.
            const priority: GlobalPlacementPriority = {
                placementSubgroupOrder,
                symbolPlacementPriority,
                symbolVariantVisibility: this.placementVariantVisible[index] ? SymbolVariantVisibility.VARIANT_VISIBLE : SymbolVariantVisibility.SYMBOL_INVISIBLE,
                symbolPlacementType: SymbolPlacementType.FIXED,
                styleLayerOrder,
                symbolDisplayOrder: 0,
            };

            // A symbol contributes its icon and/or text collision boxes as one variant.
            globalPlacement.startSymbolVariantProcessing(variantId, priority, placementRules, tileIdentity, featureId);
            if (!clipped && !shadowedByChildTile) {
                const symbolZOffsetValue = needsFeatureForZOffset && feature ? symbolZOffsetProperty.evaluate(feature, {}) : constantSymbolZOffset;
                addCollisionBox(collisionArrays.iconBox, instance, symbolZOffsetValue, () => this.getSymbolInstanceIconSize(iconZoomSize, zoom, instance.placedIconSymbolIndex));
                addCollisionBox(collisionArrays.textBox, instance, symbolZOffsetValue, () => this.getSymbolInstanceTextSize(textZoomSize, instance, zoom, index));
            }
            globalPlacement.finishVariantProcessing();
        }
    }

    updateReplacement(coord: OverscaledTileID, source: ReplacementSource): boolean {
        // Replacement has to be re-checked if the source has been updated since last time
        if (source.updateTime === this.replacementUpdateTime) {
            return false;
        }
        this.replacementUpdateTime = source.updateTime;

        // Check if replacements have changed
        const newReplacements = source.getReplacementRegionsForTile(coord.toUnwrapped(), true);
        if (regionsEquals(this.activeReplacements, newReplacements)) {
            return false;
        }
        this.activeReplacements = newReplacements;
        return true;
    }

    getResolvedImageFromTokens(tokens: string) {
        // The type of tokens comes from layer.getValueAndResolveTokens
        // But that type is wrong because we wouldn't need to do
        // this otherwise
        if (typeof tokens === "string") {
            return ResolvedImage.build(tokens);
        }
        return tokens;
    }

    populate(features: Array<IndexedFeature>, options: PopulateParameters, canonical: CanonicalTileID, tileTransform: TileTransform) {
        const layer = this.layers[0];
        const layout = layer.layout;
        const isGlobe = this.projection.name === 'globe';

        const textFont = layout.get('text-font');
        const textField = layout.get('text-field');
        const iconImage = layout.get('icon-image');
        const [iconSizeScaleRangeMin, iconSizeScaleRangeMax] = layout.get('icon-size-scale-range');
        const iconScaleFactor = clamp(options.scaleFactor || 1, iconSizeScaleRangeMin, iconSizeScaleRangeMax);
        const [textSizeScaleRangeMin, textSizeScaleRangeMax] = layout.get('text-size-scale-range');
        const textScaleFactor = clamp(options.scaleFactor || 1, textSizeScaleRangeMin, textSizeScaleRangeMax);
        const hasText =

            (textField.value.kind !== 'constant' ||

                (textField.value.value instanceof Formatted && !textField.value.value.isEmpty()) ||

                textField.value.value.toString().length > 0) &&

            (textFont.value.kind !== 'constant' || textFont.value.value.length > 0);
        // we should always resolve the icon-image value if the property was defined in the style
        // this allows us to fire the styleimagemissing event if image evaluation returns null
        // the only way to distinguish between null returned from a coalesce statement with no valid images
        // and null returned because icon-image wasn't defined is to check whether or not iconImage.parameters is an empty object

        const hasIcon = iconImage.value.kind !== 'constant' || !!iconImage.value.value || Object.keys(iconImage.parameters).length > 0;
        const hasAppearanceIcons = this.hasAnyAppearanceLayoutProperty('icon-image');
        const hasAppearances = this.layers[0].getAppearances().length > 0;

        const symbolSortKey = layout.get('symbol-sort-key');

        this.features = [];
        this.featureAppearanceData = new Map();

        if (!hasText && !hasIcon && !hasAppearanceIcons) {
            return;
        }

        const icons = options.iconDependencies;
        const stacks = options.glyphDependencies;
        const availableImages = options.availableImages;
        const globalProperties = new EvaluationParameters(this.zoom, {worldview: this.worldview, activeFloors: options.activeFloors});
        const addImageVariantToIcons = (variant: ImageVariant) => {
            const variantId = variant.id.toString();
            if (icons.has(variantId)) {
                icons.get(variantId).push(variant);
            } else {
                icons.set(variantId, [variant]);
            }
        };

        for (const indexedFeature of features) {
            const {feature, id, index, sourceLayerIndex} = indexedFeature;

            const needGeometry = layer._featureFilter.needGeometry;
            const evaluationFeature = toEvaluationFeature(feature, needGeometry);
            if (!layer._featureFilter.filter(globalProperties, evaluationFeature, canonical)) {
                continue;
            }

            evaluationFeature.geometry = loadRenderGeometry(feature, evaluationFeature, needGeometry, canonical, tileTransform);

            if (isGlobe && feature.type !== 1 && canonical.z <= 5) {
                // Resample long lines and polygons in globe view so that their length wont exceed ~0.19 radians (360/32 degrees).
                // Otherwise lines could clip through the globe as the resolution is not enough to represent curved paths.
                // The threshold value follows subdivision size used with fill extrusions
                const geom = evaluationFeature.geometry;

                // cos(11.25 degrees) = 0.98078528056
                const cosAngleThreshold = 0.98078528056;
                const predicate = (a: Point, b: Point) => {
                    const v0 = tileCoordToECEF(a.x, a.y, canonical, 1);
                    const v1 = tileCoordToECEF(b.x, b.y, canonical, 1);
                    return vec3.dot(v0, v1) < cosAngleThreshold;
                };

                for (let i = 0; i < geom.length; i++) {
                    geom[i] = resamplePred(geom[i], predicate);
                }
            }

            let text: Formatted | undefined;
            if (hasText) {
                // Expression evaluation will automatically coerce to Formatted
                // but plain string token evaluation skips that pathway so do the
                // conversion here.
                const resolvedTokens = layer.getValueAndResolveTokens('text-field', evaluationFeature, canonical, availableImages);
                const formattedText = Formatted.factory(resolvedTokens);
                // RTL text is skipped until the worker loads RTL support and populates the bucket again
                const isRTL = containsRTLText(formattedText);
                if (isRTL) this.hasRTLText = true;
                if (!isRTL || rtl) text = transformText(formattedText, layer, evaluationFeature);
            }

            let icon: ResolvedImage | null | undefined;
            if (hasIcon) {
                // Expression evaluation will automatically coerce to Image
                // but plain string token evaluation skips that pathway so do the
                // conversion here.
                const resolvedTokens = layer.getValueAndResolveTokens('icon-image', evaluationFeature, canonical, availableImages);
                icon = this.getResolvedImageFromTokens(resolvedTokens);
            }

            const symbolLayer = this.layers[0];
            let usesAppearanceIconAsFallback = false;
            if (!icon && hasAppearanceIcons) {
                const appearances = symbolLayer.getAppearances();
                for (const appearance of appearances) {
                    const iconImage = appearance.getLayoutProperty('icon-image');
                    if (iconImage) {
                        const resolvedTokens = symbolLayer.getAppearanceValueAndResolveTokens(appearance, 'icon-image', evaluationFeature, canonical, availableImages);
                        if (resolvedTokens) {
                            icon = this.getResolvedImageFromTokens(resolvedTokens as string);
                            usesAppearanceIconAsFallback = true;
                            break;
                        }
                    }
                }
            }

            if (!text && !icon) {
                continue;
            }
            const sortKey = this.sortFeaturesByKey ?

                symbolSortKey.evaluate(evaluationFeature, {}, canonical) :
                undefined;

            const symbolFeature: SymbolFeature = {
                id,
                text,
                icon,
                index,
                sourceLayerIndex,
                geometry: evaluationFeature.geometry,
                properties: feature.properties,
                type: vectorTileFeatureTypes[feature.type],
                sortKey
            };
            this.features.push(symbolFeature);

            // Store minimal data needed for appearance evaluation (setting appearances re-lays out the tile, so
            // layers without them never need it). Use the promoted ID from IndexedFeature instead of raw feature.id
            if (hasAppearances) this.featureAppearanceData.set(index, {
                id, // This is already the promoted ID from IndexedFeature
                properties: feature.properties,
                usesAppearanceIconAsPlaceholder: usesAppearanceIconAsFallback,
                // isUsingAppearance*VertexData / layoutBased*VertexData /
                // activeAppearanceIndex intentionally unset — they're mutated
                // exclusively on the main thread, so serializing defaults from
                // the worker is pure overhead.
            });

            if (icon) {
                const unevaluatedLayoutValues = symbolLayer._unevaluatedLayout._values;
                const {iconPrimary, iconSecondary} = getScaledImageVariant(icon, this.iconSizeData, unevaluatedLayoutValues['icon-size'], canonical, this.zoom, symbolFeature, this.pixelRatio, iconScaleFactor, this.worldview, availableImages);
                addImageVariantToIcons(iconPrimary);

                if (iconSecondary) {
                    this.hasAnySecondaryIcon = true;
                    addImageVariantToIcons(iconSecondary);
                }
            }

            const appearances = symbolLayer.getAppearances();
            if (appearances.length !== 0) {
                appearances.forEach(a => {
                    const iconImage = a.getLayoutProperty('icon-image');
                    if (!iconImage) return;
                    const {iconPrimary, iconSecondary} = this.getCombinedIconVariants(a, symbolLayer, evaluationFeature, canonical, availableImages, symbolFeature, iconScaleFactor);
                    if (!iconPrimary) return;
                    addImageVariantToIcons(iconPrimary);
                    if (iconSecondary) {
                        this.hasAnySecondaryIcon = true;
                        addImageVariantToIcons(iconSecondary);
                    }
                });
            }

            if (text) {
                const fontStack = textFont.evaluate(evaluationFeature, {}, canonical).join(',');
                const textAlongLine = layout.get('text-rotation-alignment') === 'map' && layout.get('symbol-placement') !== 'point';
                this.allowVerticalPlacement = this.writingModes && this.writingModes.includes(WritingMode.vertical);
                for (const section of text.sections) {
                    if (!section.image) {
                        const doesAllowVerticalWritingMode = allowsVerticalWritingMode(text.toString());
                        const sectionFont = section.fontStack || fontStack;
                        const sectionStack: Record<number, boolean> = stacks[sectionFont] = stacks[sectionFont] || {};
                        this.calculateGlyphDependencies(section.text, sectionStack, textAlongLine, this.allowVerticalPlacement, doesAllowVerticalWritingMode);
                    } else {
                        const imagePrimary = section.image.getPrimary().scaleSelf(this.pixelRatio * textScaleFactor);
                        const imagePrimaryId = imagePrimary.id.toString();
                        const primaryIcons = icons.get(imagePrimaryId) || [];
                        primaryIcons.push(imagePrimary);
                        icons.set(imagePrimaryId, primaryIcons);
                    }
                }
            }
        }

        if (layout.get('symbol-placement') === 'line') {
            // Merge adjacent lines with the same text to improve labelling.
            // It's better to place labels on one long line than on many short segments.
            this.features = mergeLines(this.features);
        }

        if (layout.get('symbol-elevation-reference') === 'hd-road-markup') {
            this.elevationType = 'road';
            if (this.hdExt) {
                this.hdExt.configureCrossSource(
                    canonical,
                    options.elevationParams,
                    options.crossSourceElevationEnabled,
                    options.terrainEnabled,
                );
                if (options.elevationFeatures && options.elevationFeatures.length > 0) {
                    this.hdExt.addElevationFeatures(options.elevationFeatures, canonical);
                }
            }
        } else if (layout.get('symbol-z-elevate')) {
            this.elevationType = 'offset';
        }
        if (this.elevationType !== 'none') {
            this.zOffsetBuffersNeedUpload = true;
        }

        if (this.sortFeaturesByKey) {
            this.features.sort((a, b) => {
                // a.sortKey is always a number when sortFeaturesByKey is true
                return (a.sortKey) - (b.sortKey);
            });
        }
    }

    getCombinedIconVariants(appearance: SymbolAppearance, layer: SymbolStyleLayer, evaluationFeature: EvaluationFeature, canonical: CanonicalTileID, availableImages: ImageId[],
        symbolFeature: SymbolFeature, iconScaleFactor: number
    ): {iconPrimary: ImageVariant | undefined; iconSecondary: ImageVariant | undefined} {
        let icon: ResolvedImage;
        if (appearance.hasLayoutProperty('icon-image')) {
            const resolvedTokens = layer.getAppearanceValueAndResolveTokens(appearance, 'icon-image', evaluationFeature, canonical, availableImages);
            icon = this.getResolvedImageFromTokens(resolvedTokens as string);
        } else {
            const resolvedTokens = layer.getValueAndResolveTokens('icon-image', evaluationFeature, canonical, availableImages);
            icon = this.getResolvedImageFromTokens(resolvedTokens);
        }

        if (icon) {
            const unevaluatedIconSize = (appearance.hasLayoutProperty('icon-size') ?
                appearance.getUnevaluatedLayoutProperty('icon-size') :
                layer._unevaluatedLayout._values['icon-size']) as PropertyValue<number, PossiblyEvaluatedPropertyValue<number>>;
            const iconSizeData = getSizeData(this.zoom, unevaluatedIconSize, this.worldview, availableImages);
            const {iconPrimary, iconSecondary} = getScaledImageVariant(icon, iconSizeData, unevaluatedIconSize, canonical, this.zoom, symbolFeature, this.pixelRatio, iconScaleFactor, this.worldview, availableImages);
            return {iconPrimary, iconSecondary};
        }

        return {iconPrimary: undefined, iconSecondary: undefined};
    }

    getCombinedIconPrimary(appearance: SymbolAppearance, layer: SymbolStyleLayer, evaluationFeature: EvaluationFeature, canonical: CanonicalTileID, availableImages: ImageId[],
        symbolFeature: SymbolFeature, iconScaleFactor: number
    ): ImageVariant | undefined {
        return this.getCombinedIconVariants(appearance, layer, evaluationFeature, canonical, availableImages, symbolFeature, iconScaleFactor).iconPrimary;
    }

    private updateSymbolInstanceIconVertices(
        symbolInstance: SymbolInstance,
        featureData: AppearanceFeatureData,
        activeAppearanceIndex: number,
        evaluationFeature: EvaluationFeature,
        vertexOffset: number,
        ctx: AppearanceIconUpdateContext,
    ): {vertexOffsetDelta: number; hasChanges: boolean} {
        const {canonical, availableImages, globalProperties, layer, iconScaleFactor, featureState, layoutIconOffset, layoutIconSize, layoutIconRotate, iconRotatesInTileSpace} = ctx;
        if (symbolInstance.placedIconSymbolIndex < 0) {
            return {vertexOffsetDelta: 0, hasChanges: false};
        }

        if (featureData.activeAppearanceIndex === activeAppearanceIndex) {
            return {vertexOffsetDelta: symbolInstance.numIconVertices, hasChanges: false};
        }

        const activeAppearance = activeAppearanceIndex >= 0 ? layer.appearances[activeAppearanceIndex] : null;
        if (activeAppearance) {
            const minimalFeature: SymbolFeature = {
                sortKey: undefined,
                text: undefined,
                icon: null, // Will be resolved in getAppearanceIconPrimary
                index: symbolInstance.featureIndex,
                sourceLayerIndex: symbolInstance.featureIndex,
                geometry: [],
                properties: featureData.properties as Record<string, string | number | boolean>,
                type: 'Point',
                id: featureData.id
            };

            const {iconPrimary, iconSecondary} = this.getCombinedIconVariants(activeAppearance, layer, evaluationFeature, canonical, availableImages, minimalFeature, iconScaleFactor);
            if (!iconPrimary) return {vertexOffsetDelta: 0, hasChanges: false};

            const primaryImageSerialized = iconPrimary.toString();
            const position = this.iconAtlasPositions && this.iconAtlasPositions.get(primaryImageSerialized);

            if (position) {
                // Get values from appearance and fallback to layout ones
                const {appearanceIconOffset: iconOffset, appearanceIconRotate} = getAppearanceIconValues(activeAppearance, layer, evaluationFeature as SymbolFeature, canonical, layoutIconOffset, layoutIconRotate, layoutIconSize, iconScaleFactor);
                // Same north offset as the layout rotation in `addSymbol`.
                const iconRotate = appearanceIconRotate + (iconRotatesInTileSpace ?
                    getNorthOffset(this.getTileTransform(canonical), symbolInstance.tileAnchorX, symbolInstance.tileAnchorY) : 0);
                const iconAnchor = layer.layout.get('icon-anchor').evaluate(evaluationFeature, featureState, canonical);

                // Resolve secondary position for cross-fade support
                const secondaryPosition = iconSecondary ? (this.iconAtlasPositions && this.iconAtlasPositions.get(iconSecondary.toString())) : undefined;
                let shapedIcon = shapeIcon(position, secondaryPosition, iconOffset, iconAnchor);

                const isSDFIcon = position.sdf;
                // Get icon-text-fit from layout since we don't support it in appearances
                const iconTextFit = layer.layout.get('icon-text-fit').constantOr('none');

                // Note: This needs to happen after text has been updated in appearances since it can change featureData.textShaping
                // Use featureData.fontScale which converts the glyph-space bounds to pixels at the appearance text-size
                if (iconTextFit !== 'none' && featureData.textShaping && featureData.iconTextFitPadding && featureData.fontScale) {
                    shapedIcon = fitIconToText(shapedIcon, featureData.textShaping, iconTextFit,
                        featureData.iconTextFitPadding, iconOffset, featureData.fontScale);
                }

                // Get appearance-specific icon-size and calculate combined scale factor
                const effectiveIconSize = this.calculateEffectiveAppearanceIconSize(
                    activeAppearance,
                    globalProperties.zoom,
                    evaluationFeature,
                    canonical,
                    availableImages,
                    iconScaleFactor,
                    layoutIconSize
                );
                const newSizeX = 0; // Unused in appearances
                const newSizeY = (Math.min(MAX_PACKED_SIZE, Math.round(effectiveIconSize * SIZE_PACK_FACTOR)) << 1) + 1; // pack isAppearance flag in lowest bit
                // Generate new icon quads with updated texture coordinates and size
                const iconQuads = getIconQuads(shapedIcon, iconRotate, isSDFIcon, iconTextFit !== 'none', iconScaleFactor);

                const hasTransitioning = this.icon.iconTransitioningVertexArray.length > 0;

                // Store the layout-based vertex data to restore it later if it's the first time we use an appearance for this feature
                if (!featureData.isUsingAppearanceIconVertexData) {
                    featureData.isUsingAppearanceIconVertexData = true;
                    featureData.layoutBasedIconVertexData = this.icon.snapshotSymbolVertexData(vertexOffset, symbolInstance.numIconVertices);
                    if (hasTransitioning) {
                        featureData.layoutBasedIconTransitioningVertexData = this.icon.snapshotIconTransitioningVertexData(vertexOffset, symbolInstance.numIconVertices);
                    }
                }

                // Update vertex data - only update as many quads as were allocated during layout
                const maxQuads = Math.floor(symbolInstance.numIconVertices / 4);
                const quadsToUpdate = Math.min(iconQuads.length, maxQuads);

                let currentVertexOffset = vertexOffset;
                const transitioningUint16 = hasTransitioning ? this.icon.iconTransitioningVertexArray.uint16 : null;
                for (let j = 0; j < quadsToUpdate; ++j) {
                    const quad = iconQuads[j];

                    // Since icon-anchor is not supported in appearances we use the first layout quad one
                    // Note that layout quads might not exist, in that case we use the symbolInstance
                    // tileAnchor.
                    const anchorX = featureData.layoutBasedIconVertexData[0] || symbolInstance.tileAnchorX;
                    const anchorY = featureData.layoutBasedIconVertexData[1] || symbolInstance.tileAnchorY;
                    const pixelOffsetTLX = quad.pixelOffsetTL.x * 16;
                    const pixelOffsetTLY = quad.pixelOffsetTL.y * 16;
                    const pixelOffsetBRX = quad.pixelOffsetBR.x * 16;
                    const pixelOffsetBRY = quad.pixelOffsetBR.y * 16;
                    const minFontScaleX = quad.minFontScaleX * 16;
                    const minFontScaleY = quad.minFontScaleY * 16;

                    this.icon.updateSymbolVertexData(currentVertexOffset, anchorX, anchorY, Math.round(quad.tl.x * 32), Math.round(quad.tl.y * 32), quad.texPrimary.x, quad.texPrimary.y, newSizeX, newSizeY, pixelOffsetTLX, pixelOffsetTLY, minFontScaleX, minFontScaleY);
                    this.icon.updateSymbolVertexData(currentVertexOffset + 1, anchorX, anchorY, Math.round(quad.tr.x * 32), Math.round(quad.tr.y * 32), quad.texPrimary.x + quad.texPrimary.w, quad.texPrimary.y, newSizeX, newSizeY, pixelOffsetBRX, pixelOffsetTLY, minFontScaleX, minFontScaleY);
                    this.icon.updateSymbolVertexData(currentVertexOffset + 2, anchorX, anchorY, Math.round(quad.bl.x * 32), Math.round(quad.bl.y * 32), quad.texPrimary.x, quad.texPrimary.y + quad.texPrimary.h, newSizeX, newSizeY, pixelOffsetTLX, pixelOffsetBRY, minFontScaleX, minFontScaleY);
                    this.icon.updateSymbolVertexData(currentVertexOffset + 3, anchorX, anchorY, Math.round(quad.br.x * 32), Math.round(quad.br.y * 32), quad.texPrimary.x + quad.texPrimary.w, quad.texPrimary.y + quad.texPrimary.h, newSizeX, newSizeY, pixelOffsetBRX, pixelOffsetBRY, minFontScaleX, minFontScaleY);

                    // Update secondary (cross-fade) texture coords: fall back to primary when no secondary.
                    if (transitioningUint16) {
                        const tex = quad.texSecondary ? quad.texSecondary : quad.texPrimary;
                        const base = currentVertexOffset * ICON_TRANSITIONING_STRIDE;
                        transitioningUint16[base]     = tex.x;
                        transitioningUint16[base + 1] = tex.y;
                        transitioningUint16[base + 2] = tex.x + tex.w;
                        transitioningUint16[base + 3] = tex.y;
                        transitioningUint16[base + 4] = tex.x;
                        transitioningUint16[base + 5] = tex.y + tex.h;
                        transitioningUint16[base + 6] = tex.x + tex.w;
                        transitioningUint16[base + 7] = tex.y + tex.h;
                    }

                    currentVertexOffset += 4;
                }

                // Clear remaining vertices to prevent rendering stale data from previous appearances
                const verticesUsed = quadsToUpdate * 4;
                const remainingVertices = symbolInstance.numIconVertices - verticesUsed;
                for (let k = 0; k < remainingVertices; ++k) {
                    this.icon.updateSymbolVertexData(currentVertexOffset + k, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0);
                }

                return {vertexOffsetDelta: symbolInstance.numIconVertices, hasChanges: true};
            }
        } else if (featureData.usesAppearanceIconAsPlaceholder) {
            // Hide the placeholder icon when no appearance is active: zero out texture coordinates
            // so the quad is invisible while the vertex buffer structure is preserved.
            // usesAppearanceIconAsPlaceholder can only be true when appearances existed at populate() time.
            this.icon.updateSymbolVertexData(vertexOffset, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0);
            this.icon.updateSymbolVertexData(vertexOffset + 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0);
            this.icon.updateSymbolVertexData(vertexOffset + 2, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0);
            this.icon.updateSymbolVertexData(vertexOffset + 3, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0);
            return {vertexOffsetDelta: symbolInstance.numIconVertices, hasChanges: true};
        } else if (featureData.isUsingAppearanceIconVertexData) {
            // No active appearance but vertex buffer still holds appearance data — restore original layout vertices.
            this.icon.restoreSymbolVertexData(vertexOffset, featureData.layoutBasedIconVertexData);
            if (featureData.layoutBasedIconTransitioningVertexData) {
                this.icon.restoreIconTransitioningVertexData(vertexOffset, featureData.layoutBasedIconTransitioningVertexData);
            }
            featureData.isUsingAppearanceIconVertexData = false;
            return {vertexOffsetDelta: featureData.layoutBasedIconVertexData.length / SYMBOL_VERTEX_STRIDE, hasChanges: true};
        }
        return {vertexOffsetDelta: symbolInstance.numIconVertices, hasChanges: false};
    }

    private updateSymbolInstanceTextVertices(
        symbolInstance: SymbolInstance,
        featureData: AppearanceFeatureData,
        activeAppearanceIndex: number,
        evaluationFeature: EvaluationFeature,
        vertexOffset: number,
        ctx: AppearanceTextUpdateContext,
    ): {vertexOffsetDelta: number; hasChanges: boolean} {
        const {canonical, layer, textScaleFactor, imageMap, featureState, layoutTextOffset, layoutTextSize, layoutTextRotate, textRotatesInTileSpace, layoutMinZoomSize, layoutMaxZoomSize, layoutTextSizeMinZoom, layoutTextSizeMaxZoom, availableImages} = ctx;
        const hasHorizontalText = symbolInstance.numHorizontalGlyphVertices > 0;
        const hasVerticalText = symbolInstance.numVerticalGlyphVertices > 0;
        const hasText = hasHorizontalText || hasVerticalText;

        if (!hasText) {
            return {vertexOffsetDelta: hasText ? symbolInstance.numHorizontalGlyphVertices + symbolInstance.numVerticalGlyphVertices : 0, hasChanges: false};
        }

        if (featureData.activeAppearanceIndex === activeAppearanceIndex) {
            return {vertexOffsetDelta: symbolInstance.numHorizontalGlyphVertices + symbolInstance.numVerticalGlyphVertices, hasChanges: false};
        }

        const activeAppearance = activeAppearanceIndex >= 0 ? layer.appearances[activeAppearanceIndex] : null;
        if (activeAppearance && featureData.textShaping) {
            // Get appearance-based text properties and fallback to layout ones
            const {appearanceTextOffset: textOffset, appearanceTextRotate, appearanceTextSize: textSizeValue} = getAppearanceTextValues(activeAppearance, layer, evaluationFeature as SymbolFeature, canonical, layoutTextOffset, layoutTextRotate, layoutTextSize);
            // Same north offset as the layout rotation in `addSymbol`.
            const textRotate = appearanceTextRotate + (textRotatesInTileSpace ?
                getNorthOffset(this.getTileTransform(canonical), symbolInstance.tileAnchorX, symbolInstance.tileAnchorY) : 0);

            // Recompute fontScale with appearance data so it will be correctly used during icon update later
            featureData.fontScale = computeFontScale(textSizeValue, featureData.textScaleFactor);
            const unevaluatedTextSize = activeAppearance.getUnevaluatedLayoutProperty('text-size') as PropertyValue<number, PossiblyEvaluatedPropertyValue<number>>;
            let textSizeData = this.textSizeData;
            let minZoom = layoutTextSizeMinZoom;
            let maxZoom = layoutTextSizeMaxZoom;
            if (activeAppearance.hasLayoutProperty('text-size')) {
                textSizeData = getSizeData(this.zoom, unevaluatedTextSize, this.worldview, availableImages);
                minZoom = this.textSizeData.kind === 'composite' ? this.textSizeData.minZoom : 0;
                maxZoom = this.textSizeData.kind === 'composite' ? this.textSizeData.maxZoom : 0;
            }

            // Update the shaped text offset
            // When the text is not on lines, top/bottom/left/right are basically
            // the layout offset. When text is on a line, it will have been translated
            // and we need to compute the offset from the layout offset and apply it
            // to the new textOffset
            const topDiff = featureData.textShaping.top - layoutTextOffset[1];
            const bottomDiff = featureData.textShaping.bottom - layoutTextOffset[1];
            const leftDiff = featureData.textShaping.left - layoutTextOffset[0];
            const rightDiff = featureData.textShaping.right - layoutTextOffset[0];

            // Update the offset but DON'T scale the bounds - keep them in glyph space
            // fitIconToText will apply the fontScale to convert to pixels at the new text size
            featureData.textShaping.top = textOffset[1] + topDiff;
            featureData.textShaping.bottom = textOffset[1] + bottomDiff;
            featureData.textShaping.left = textOffset[0] + leftDiff;
            featureData.textShaping.right = textOffset[0] + rightDiff;

            // Generate new text quads with appearance properties
            const textQuads = getGlyphQuads(
                {x: symbolInstance.tileAnchorX, y: symbolInstance.tileAnchorY} as Anchor,
                featureData.textShaping,
                textOffset,
                layer,
                false,
                evaluationFeature,
                imageMap,
                this.allowVerticalPlacement,
                textRotate
            );

            // Calculate size vertex data with appearance text-size
            const minZoomSize = textSizeValue && textSizeData.kind === 'composite' ? unevaluatedTextSize.possiblyEvaluate(new EvaluationParameters(minZoom, {}), canonical).evaluate(evaluationFeature, featureState, canonical) : layoutMinZoomSize;
            const maxZoomSize = textSizeValue && textSizeData.kind === 'composite' ? unevaluatedTextSize.possiblyEvaluate(new EvaluationParameters(maxZoom, {}), canonical).evaluate(evaluationFeature, featureState, canonical) : layoutMaxZoomSize;
            const textSizeDataKind = packSizeForVertex(activeAppearance.name, textSizeData, textSizeValue, textScaleFactor, minZoomSize, maxZoomSize);
            const textSizeY = Array.isArray(textSizeDataKind) ? textSizeDataKind[1] : textSizeValue;
            const symbolSizeX = 0; // Not used in appearances
            const symbolSizeY = (Math.min(MAX_PACKED_SIZE, Math.round(textSizeY * SIZE_PACK_FACTOR)) << 1) + 1; // Appearance flag

            // Store the layout-based vertex data to restore it later if it's the first time we use an appearance for this feature
            if (!featureData.isUsingAppearanceTextVertexData) {
                featureData.isUsingAppearanceTextVertexData = true;
                featureData.layoutBasedTextVertexData = this.text.snapshotSymbolVertexData(vertexOffset, symbolInstance.numHorizontalGlyphVertices + symbolInstance.numVerticalGlyphVertices);
            }

            // Update vertex data for text quads
            for (let j = 0; j < textQuads.length && j < (symbolInstance.numHorizontalGlyphVertices + symbolInstance.numVerticalGlyphVertices) / 4; ++j) {
                const quad = textQuads[j];
                const y = quad.glyphOffset[1];

                const anchorX = symbolInstance.tileAnchorX;
                const anchorY = symbolInstance.tileAnchorY;

                // Update 4 vertices for this quad
                const vertexIndex = vertexOffset + j * 4;
                this.text.updateSymbolVertexData(vertexIndex, anchorX, anchorY, Math.round(quad.tl.x * 32), Math.round((y + quad.tl.y) * 32), quad.texPrimary.x, quad.texPrimary.y, symbolSizeX, symbolSizeY, quad.pixelOffsetTL.x, quad.pixelOffsetTL.y, quad.minFontScaleX, quad.minFontScaleY);
                this.text.updateSymbolVertexData(vertexIndex + 1, anchorX, anchorY, Math.round(quad.tr.x * 32), Math.round((y + quad.tr.y) * 32), quad.texPrimary.x + quad.texPrimary.w, quad.texPrimary.y, symbolSizeX, symbolSizeY, quad.pixelOffsetBR.x, quad.pixelOffsetTL.y, quad.minFontScaleX, quad.minFontScaleY);
                this.text.updateSymbolVertexData(vertexIndex + 2, anchorX, anchorY, Math.round(quad.bl.x * 32), Math.round((y + quad.bl.y) * 32), quad.texPrimary.x, quad.texPrimary.y + quad.texPrimary.h, symbolSizeX, symbolSizeY, quad.pixelOffsetTL.x, quad.pixelOffsetBR.y, quad.minFontScaleX, quad.minFontScaleY);
                this.text.updateSymbolVertexData(vertexIndex + 3, anchorX, anchorY, Math.round(quad.br.x * 32), Math.round((y + quad.br.y) * 32), quad.texPrimary.x + quad.texPrimary.w, quad.texPrimary.y + quad.texPrimary.h, symbolSizeX, symbolSizeY, quad.pixelOffsetBR.x, quad.pixelOffsetBR.y, quad.minFontScaleX, quad.minFontScaleY);
            }

            return {vertexOffsetDelta: symbolInstance.numHorizontalGlyphVertices + symbolInstance.numVerticalGlyphVertices, hasChanges: true};
        } else if (featureData.isUsingAppearanceTextVertexData) {
            // No active appearance but vertex buffer still holds appearance data — restore original layout vertices.
            this.text.restoreSymbolVertexData(vertexOffset, featureData.layoutBasedTextVertexData);
            featureData.isUsingAppearanceTextVertexData = false;
            return {vertexOffsetDelta: featureData.layoutBasedTextVertexData.length / SYMBOL_VERTEX_STRIDE, hasChanges: true};
        } else {
            return {vertexOffsetDelta: symbolInstance.numHorizontalGlyphVertices + symbolInstance.numVerticalGlyphVertices, hasChanges: false};
        }
    }

    updateExpressions(layers: ReadonlyArray<TypedStyleLayer>) {
        if (this.text) this.text.programConfigurations.updateExpressions(layers);
        if (this.icon) this.icon.programConfigurations.updateExpressions(layers);
    }

    update(states: FeatureStates, vtLayer: VectorTileLayer, availableImages: ImageId[], imagePositions: SpritePositions, layers: ReadonlyArray<TypedStyleLayer>, isBrightnessChanged: boolean, brightness?: number | null, canonical?: CanonicalTileID) {
        // Traditional attribute-based updates, skipped when UBO is active
        if (!this.text.uboBinder) {
            this.text.programConfigurations.updatePaintArrays(states, vtLayer, layers, availableImages, imagePositions, isBrightnessChanged, brightness, this.worldview);
        }
        if (!this.icon.uboBinder) {
            this.icon.programConfigurations.updatePaintArrays(states, vtLayer, layers, availableImages, imagePositions, isBrightnessChanged, brightness, this.worldview);
        }

        // UBO-based updates
        if (canonical) {
            // Update all features when brightness or other dynamic expressions change.
            // Skip when all data-driven properties are light-constant: brightness doesn't affect
            // stored UBO values in that case (constant uniforms handle the rendering adjustment).
            if (isBrightnessChanged) {
                for (const binder of [this.text.uboBinder, this.icon.uboBinder]) {
                    if (binder && !binder.isLightConstant) {
                        binder.updateDynamicExpressions(layers[0] as SymbolStyleLayer, vtLayer, canonical, availableImages, states, brightness);
                    }
                }
            } else if (Object.keys(states).length > 0) {
                // Update specific features when feature-state changes.
                // Skip when no paint property reads feature-state — appearance condition
                // flips (which CAN depend on state) are written by updateAppearances →
                // updateFeaturePaintForAppearance below, so updateFeatures would only
                // re-evaluate values that are about to be overwritten with the same data.
                const symbolLayer = layers[0] as SymbolStyleLayer;
                const binders = [this.text.uboBinder, this.icon.uboBinder].filter(b => b && b.hasStateDependentPaint(symbolLayer));

                if (binders.length > 0) {
                    const featureIds = new Set<string | number>(Object.keys(states).map(id => {
                        // Convert to number only if it's a safe integer to avoid precision loss
                        const numId = Number(id);
                        if (!isNaN(numId) && Number.isSafeInteger(numId) && String(numId) === id) {
                            return numId;
                        }
                        return id;
                    }));

                    for (const binder of binders) {
                        binder.updateFeatures(featureIds, symbolLayer, vtLayer, canonical, availableImages, states, brightness);
                    }
                }
            }
        }
    }

    updateZOffset() {
        // z offset changes less often than placement opacity, so only rebuild and upload when flagged
        if (!this.zOffsetBuffersNeedUpload) return;
        this.zOffsetBuffersNeedUpload = false;
        // once uploaded, rebuild into scratch arrays shared by all buckets instead of keeping a copy per bucket
        const textArray = this.text.zOffsetVertexBuffer ? textZOffsetScratch : this.text.zOffsetVertexArray;
        const iconArray = this.icon.zOffsetVertexBuffer ? iconZOffsetScratch : this.icon.zOffsetVertexArray;
        textArray.clear();
        iconArray.clear();
        for (let s = 0; s < this.symbolInstances.length; s++) {
            const symbolInstance = this.symbolInstances.get(s);
            const zOffset = symbolInstance.zOffset;
            appendZOffset(textArray, symbolInstance.numHorizontalGlyphVertices, zOffset);
            appendZOffset(textArray, symbolInstance.numVerticalGlyphVertices, zOffset);
            if (symbolInstance.numIconVertices > 0) {
                if (symbolInstance.placedIconSymbolIndex >= 0) {
                    appendZOffset(iconArray, symbolInstance.numIconVertices, zOffset);
                }
                if (symbolInstance.verticalPlacedIconSymbolIndex >= 0) {
                    appendZOffset(iconArray, symbolInstance.numVerticalIconVertices, zOffset);
                }
            }
        }

        if (this.text.zOffsetVertexBuffer) {
            this.text.zOffsetVertexBuffer.updateData(textArray);
            assert(this.text.zOffsetVertexBuffer.length === this.text.layoutVertexArray.length);
        }
        if (this.icon.zOffsetVertexBuffer) {
            this.icon.zOffsetVertexBuffer.updateData(iconArray);
            assert(this.icon.zOffsetVertexBuffer.length === this.icon.layoutVertexArray.length);
        }
    }

    isEmpty(): boolean {
        return this.symbolInstances.length === 0;
    }

    uploadPending(): boolean {
        return !this.uploaded || this.text.programConfigurations.needsUpload || this.icon.programConfigurations.needsUpload;
    }

    upload(context: Context, canonical?: CanonicalTileID, featureState?: FeatureStates, availableImages?: Array<ImageId>, globalProperties?: GlobalProperties) {
        if (!this.uploaded && this.hasDebugData()) {
            this.textCollisionBox.upload(context);
            this.iconCollisionBox.upload(context);
        }

        this.text.upload(context, this.sortFeaturesByY, !this.uploaded, this.text.programConfigurations.needsUpload, this.zOffsetBuffersNeedUpload, this.hasAppearances);

        if (this.hasAppearances === null) {
            this.hasAppearances = this.layers.some(layer => layer.appearances && layer.appearances.length > 0);
        }
        this.icon.upload(context, this.sortFeaturesByY, !this.uploaded, this.icon.programConfigurations.needsUpload, this.zOffsetBuffersNeedUpload, this.hasAppearances);
        this.uploaded = true;
    }

    updateAppearances(canonical?: CanonicalTileID, featureState?: FeatureStates, availableImages?: Array<ImageId>, globalProperties?: GlobalProperties, imageManager?: ImageManager, featureStateChanged: boolean = false) {
        // Note: featureState can be undefined or {} for features without feature-state
        // Appearances can activate based on zoom/pitch alone without feature-state
        const noChanges = {hasLayoutChanges: false, hasUboChanges: false};
        if (!canonical || !availableImages || !this.featureAppearanceData) {
            return noChanges;
        }
        // Ensure featureState is at least an empty object for property evaluation
        const states = featureState || {};

        const hasIconData = this.icon.layoutVertexArray && this.icon.layoutVertexArray.length > 0 && this.icon.layoutVertexArray.arrayBuffer;
        const hasTextData = this.text.layoutVertexArray && this.text.layoutVertexArray.length > 0 && this.text.layoutVertexArray.arrayBuffer;

        if (!hasIconData && !hasTextData) {
            return noChanges;
        }

        const layer = this.layers[0];
        const layout = layer.layout;

        // Create or recreate FeatureAppearances when the layer's appearances change structurally.
        if (!this.featureAppearances ||
            this.featureAppearances.appearancesVersion !== layer.appearancesVersion) {
            this.featureAppearances = new FeatureAppearances(layer.appearances, layer.appearancesVersion);
        }

        const appearanceUpdateResult: AppearanceUpdateResult = this.featureAppearances.update(
            globalProperties, featureStateChanged
        );
        if (appearanceUpdateResult.kind === 'no-changes') return noChanges;

        // Prepare icon parameters
        let iconScaleFactor = 1;
        let iconVertexOffset = 0;
        let hasIconChanges = false;
        let hasUboChanges = false;
        if (hasIconData) {
            const [iconSizeScaleRangeMin, iconSizeScaleRangeMax] = layout.get('icon-size-scale-range');
            iconScaleFactor = clamp(1, iconSizeScaleRangeMin, iconSizeScaleRangeMax);
        }
        const iconRotatesInTileSpace = hasIconData && rotatesInTileSpace(layer, 'icon');

        // Prepare text parameters
        let textScaleFactor = 1;
        let textVertexOffset = 0;
        let hasTextChanges = false;
        if (hasTextData) {
            const [textSizeScaleRangeMin, textSizeScaleRangeMax] = layout.get('text-size-scale-range');
            textScaleFactor = clamp(1, textSizeScaleRangeMin, textSizeScaleRangeMax);
        }
        const textRotatesInTileSpace = hasTextData && rotatesInTileSpace(layer, 'text');

        // Construct imageMap from imageManager for inline images in formatted text
        const imageMap = new Map<StringifiedImageVariant, StyleImage>();
        if (imageManager && availableImages) {
            for (const imageId of availableImages) {
                const image = imageManager.getImage(imageId, layer.scope);
                if (image) {
                    // Create a default ImageVariant from the ImageId
                    const imageVariant = new ImageVariantClass(imageId.toString());
                    imageMap.set(imageVariant.toString(), image);
                }
            }
        }

        const uboBinders = [this.text.uboBinder, this.icon.uboBinder];

        for (let s = 0; s < this.symbolInstances.length; s++) {
            const symbolInstance = this.symbolInstances.get(s);
            const featureData = this.getAppearanceFeatureData(symbolInstance.featureIndex);
            if (!featureData) continue;

            const featureId = featureData.id;
            const featureStateForThis = featureId !== undefined ? states[String(featureId)] : undefined;

            // Create a evaluation feature. Note that the only important parts are the id and properties
            const evaluationFeature = {
                type: 'Point' as const, // Type doesn't matter
                id: featureData.id,
                properties: featureData.properties,
                geometry: [] // Geometry doesn't matter
            };

            let activeAppearanceIndex: number;
            if (appearanceUpdateResult.kind === 'all-features') {
                activeAppearanceIndex = appearanceUpdateResult.appearanceIndex;
            } else {
                // 'per-feature': evaluate conditions with feature context
                const found = layer.appearances ? layer.appearances.findIndex(a => a.isActive({globals: globalProperties, feature: evaluationFeature, canonical, featureState: featureStateForThis})) : -1;
                activeAppearanceIndex = found >= 0 ? found : -1;
            }
            const appearanceChanged = featureData.activeAppearanceIndex !== activeAppearanceIndex;

            if (hasTextData) {
                const layoutTextSizeExpression = layout.get('text-size');
                const textSizeDataMinZoom = this.textSizeData.kind === 'composite' ? this.textSizeData.minZoom : 0;
                const textSizeDataMaxZoom = this.textSizeData.kind === 'composite' ? this.textSizeData.maxZoom : 0;
                const textCtx: AppearanceTextUpdateContext = {
                    canonical,
                    layer,
                    featureState: featureStateForThis,
                    availableImages,
                    textScaleFactor,
                    imageMap,
                    layoutTextOffset: layout.get('text-offset').evaluate(evaluationFeature, featureStateForThis, canonical).map((v): number => v * ONE_EM) as [number, number],
                    layoutTextSize: layoutTextSizeExpression.evaluate(evaluationFeature, featureStateForThis, canonical),
                    layoutTextRotate: layout.get('text-rotate').evaluate(evaluationFeature, featureStateForThis, canonical),
                    textRotatesInTileSpace,
                    layoutMinZoomSize: layoutTextSizeExpression.evaluate(evaluationFeature, {zoom: textSizeDataMinZoom}, canonical),
                    layoutMaxZoomSize: layoutTextSizeExpression.evaluate(evaluationFeature, {zoom: textSizeDataMaxZoom}, canonical),
                    layoutTextSizeMinZoom: textSizeDataMinZoom,
                    layoutTextSizeMaxZoom: textSizeDataMaxZoom,
                };
                const textResult = this.updateSymbolInstanceTextVertices(
                    symbolInstance, featureData, activeAppearanceIndex, evaluationFeature, textVertexOffset, textCtx
                );
                textVertexOffset += textResult.vertexOffsetDelta;
                hasTextChanges = hasTextChanges || textResult.hasChanges;
            }

            if (hasIconData) {
                const iconCtx: AppearanceIconUpdateContext = {
                    canonical,
                    layer,
                    featureState: featureStateForThis,
                    availableImages,
                    globalProperties,
                    iconScaleFactor,
                    layoutIconOffset: layout.get('icon-offset').evaluate(evaluationFeature, featureStateForThis, canonical),
                    layoutIconSize: layout.get('icon-size').evaluate(evaluationFeature, featureStateForThis, canonical, availableImages),
                    layoutIconRotate: layout.get('icon-rotate').evaluate(evaluationFeature, featureStateForThis, canonical),
                    iconRotatesInTileSpace,
                };
                const iconResult = this.updateSymbolInstanceIconVertices(
                    symbolInstance, featureData, activeAppearanceIndex, evaluationFeature, iconVertexOffset, iconCtx
                );
                iconVertexOffset += iconResult.vertexOffsetDelta;
                hasIconChanges = hasIconChanges || iconResult.hasChanges;
            }

            if (appearanceChanged) {
                const vtFeatureIndex = symbolInstance.featureIndex;
                const brightness = globalProperties ? globalProperties.brightness : null;
                const fState = featureStateForThis || {};
                const activeAppearance = activeAppearanceIndex >= 0 ? layer.appearances[activeAppearanceIndex] : null;
                for (const binder of uboBinders) {
                    if (!binder) continue;
                    // updateDynamicExpressions/updateFeatures, which set the layer, may not have
                    // been called for zoom/pitch-only condition changes so we do it here
                    binder.layer = layer;
                    hasUboChanges = binder.updateFeaturePaintForAppearance(vtFeatureIndex, evaluationFeature, fState, canonical, availableImages, brightness, activeAppearance) || hasUboChanges;
                }
            }

            featureData.activeAppearanceIndex = activeAppearanceIndex;
        }

        // Update GPU buffers if there were changes
        if (hasIconChanges && this.icon.layoutVertexBuffer && this.icon.layoutVertexArray.arrayBuffer !== null) {
            if (this.icon.layoutVertexArray.length === this.icon.layoutVertexBuffer.length) {
                this.icon.layoutVertexBuffer.updateData(this.icon.layoutVertexArray);
            }
        }

        if (hasIconChanges && this.icon.iconTransitioningVertexBuffer && this.icon.iconTransitioningVertexArray.length > 0) {
            if (this.icon.iconTransitioningVertexArray.length === this.icon.iconTransitioningVertexBuffer.length) {
                this.icon.iconTransitioningVertexBuffer.updateData(this.icon.iconTransitioningVertexArray);
            }
        }

        if (hasTextChanges && this.text.layoutVertexBuffer && this.text.layoutVertexArray.arrayBuffer !== null) {
            if (this.text.layoutVertexArray.length === this.text.layoutVertexBuffer.length) {
                this.text.layoutVertexBuffer.updateData(this.text.layoutVertexArray);
            }
        }

        return {hasLayoutChanges: hasIconChanges || hasTextChanges, hasUboChanges};
    }

    destroyDebugData() {
        this.textCollisionBox.destroy();
        this.iconCollisionBox.destroy();
    }

    getProjection(): Projection {
        if (!this.projectionInstance) {
            this.projectionInstance = getProjection(this.projection);
        }
        return this.projectionInstance;
    }

    getTileTransform(canonical: CanonicalTileID): TileTransform {
        if (!this.tileTransformInstance) {
            this.tileTransformInstance = tileTransform(canonical, this.getProjection());
        }
        return this.tileTransformInstance;
    }

    destroy() {
        this.text.destroy();
        this.icon.destroy();

        if (this.hasDebugData()) {
            this.destroyDebugData();
        }
    }

    addToLineVertexArray(anchor: Anchor, line: Array<Point>): LineVertexRange {
        const lineStartIndex = this.lineVertexArray.length;
        if (anchor.segment !== undefined) {
            for (const {x, y} of line) {
                this.lineVertexArray.emplaceBack(x, y);
            }
        }
        return {
            lineStartIndex,
            lineLength: this.lineVertexArray.length - lineStartIndex
        };
    }

    addSymbols(
        arrays: SymbolBuffers,
        quads: Array<SymbolQuad>,
        sizeVertex: number[],
        lineOffset: [number, number],
        alongLine: boolean,
        feature: SymbolFeature,
        writingMode: number | undefined,
        globe: GlobeData | null | undefined,
        tileAnchor: Anchor,
        lineStartIndex: number,
        lineLength: number,
        associatedIconIndex: number,
        availableImages: ImageId[],
        canonical: CanonicalTileID,
        brightness: number | null | undefined,
        hasAnySecondaryIcon: boolean,
        symbolInstanceIndex: number,
        maxQuadCount: number
    ) {
        const indexArray = arrays.indexArray;
        const layoutVertexArray = arrays.layoutVertexArray;
        const globeExtVertexArray = arrays.globeExtVertexArray;

        // Use maxQuadCount for segment preparation to ensure enough space for appearance variants
        const quadCountForSegment = maxQuadCount;

        // Get batch index before calling prepareSegment so it can force segment boundaries
        const batchIndex = arrays.uboBinder ? arrays.uboBinder.getCurrentBatchIndex() : undefined;
        const segment = arrays.segments.prepareSegment(4 * quadCountForSegment, layoutVertexArray, indexArray, this.canOverlap ? feature.sortKey : undefined, batchIndex);

        const glyphOffsetArrayStart = this.glyphOffsetArray.length;
        const vertexStartIndex = segment.vertexLength;

        const angle = (this.allowVerticalPlacement && writingMode === WritingMode.vertical) ? Math.PI / 2 : 0;

        const sections = feature.text && feature.text.sections;

        // Track vertex count for UBO feature ID population
        let lastPopulatedVertexCount = layoutVertexArray.length;
        let lastUboIndex = -1; // Track the last UBO index for null vertex padding

        for (let i = 0; i < quads.length; i++) {
            const {tl, tr, bl, br, texPrimary, texSecondary, pixelOffsetTL, pixelOffsetBR, minFontScaleX, minFontScaleY, glyphOffset, isSDF, sectionIndex} = quads[i];
            const index = segment.vertexLength;

            const y = glyphOffset[1];
            addVertex(layoutVertexArray, tileAnchor.x, tileAnchor.y, tl.x, y + tl.y, texPrimary.x, texPrimary.y, sizeVertex, isSDF, pixelOffsetTL.x, pixelOffsetTL.y, minFontScaleX, minFontScaleY);
            addVertex(layoutVertexArray, tileAnchor.x, tileAnchor.y, tr.x, y + tr.y, texPrimary.x + texPrimary.w, texPrimary.y, sizeVertex, isSDF, pixelOffsetBR.x, pixelOffsetTL.y, minFontScaleX, minFontScaleY);
            addVertex(layoutVertexArray, tileAnchor.x, tileAnchor.y, bl.x, y + bl.y, texPrimary.x, texPrimary.y + texPrimary.h, sizeVertex, isSDF, pixelOffsetTL.x, pixelOffsetBR.y, minFontScaleX, minFontScaleY);
            addVertex(layoutVertexArray, tileAnchor.x, tileAnchor.y, br.x, y + br.y, texPrimary.x + texPrimary.w, texPrimary.y + texPrimary.h, sizeVertex, isSDF, pixelOffsetBR.x, pixelOffsetBR.y, minFontScaleX, minFontScaleY);

            if (globe) {
                const {x, y, z} = globe.anchor;
                const [ux, uy, uz] = globe.up;
                addGlobeVertex(globeExtVertexArray, x, y, z, ux, uy, uz);
                addGlobeVertex(globeExtVertexArray, x, y, z, ux, uy, uz);
                addGlobeVertex(globeExtVertexArray, x, y, z, ux, uy, uz);
                addGlobeVertex(globeExtVertexArray, x, y, z, ux, uy, uz);

                addDynamicAttributes(arrays.dynamicLayoutVertexArray, x, y, z, angle);
            } else {
                addDynamicAttributes(arrays.dynamicLayoutVertexArray, tileAnchor.x, tileAnchor.y, tileAnchor.z, angle);
            }

            // For data-driven cases if at least of one the icon has a transitionable variant
            // we have to load the main variant in cases where the secondary image is not specified
            if (hasAnySecondaryIcon) {
                const tex = texSecondary ? texSecondary : texPrimary;
                addTransitioningVertex(arrays.iconTransitioningVertexArray, tex.x, tex.y);
                addTransitioningVertex(arrays.iconTransitioningVertexArray, tex.x + tex.w, tex.y);
                addTransitioningVertex(arrays.iconTransitioningVertexArray, tex.x, tex.y + tex.h);
                addTransitioningVertex(arrays.iconTransitioningVertexArray, tex.x + tex.w, tex.y + tex.h);
            }

            indexArray.emplaceBack(index, index + 1, index + 2);
            indexArray.emplaceBack(index + 1, index + 2, index + 3);

            segment.vertexLength += 4;
            segment.primitiveLength += 2;

            this.glyphOffsetArray.emplaceBack(glyphOffset[0]);

            if (i === quads.length - 1 || sectionIndex !== quads[i + 1].sectionIndex) {
                if (!arrays.uboBinder) {
                    arrays.programConfigurations.populatePaintArrays(layoutVertexArray.length, feature, feature.index, {}, availableImages, canonical, brightness, sections && sections[sectionIndex], this.worldview);
                }

                if (arrays.uboBinder) {
                    const uboIndex = arrays.uboBinder.populateUBO(
                        feature,
                        feature.index,
                        canonical,
                        availableImages,
                        brightness,
                        sections && sections[sectionIndex]
                    );
                    lastUboIndex = uboIndex; // Track for null vertex padding
                    // Add feature ID for each vertex in this section
                    const sectionVertexCount = layoutVertexArray.length - lastPopulatedVertexCount;
                    for (let v = 0; v < sectionVertexCount; v++) {
                        arrays.featureIdArray.emplaceBack(uboIndex);
                    }
                    // Update tracking for next section
                    lastPopulatedVertexCount = layoutVertexArray.length;
                }
            }
        }

        // Add zero-padding for unused vertices to ensure consistent buffer structure for appearance variants
        const remainingQuads = maxQuadCount - quads.length;
        if (remainingQuads !== 0) {
            this._addNullVertices(remainingQuads, layoutVertexArray, sizeVertex, globe, globeExtVertexArray, arrays, hasAnySecondaryIcon, segment, indexArray);

            // Add feature IDs for null vertices so they share the last section's UBO entry
            if (arrays.uboBinder && lastUboIndex >= 0) {
                const nullVertexCount = remainingQuads * 4; // 4 vertices per quad
                for (let v = 0; v < nullVertexCount; v++) {
                    arrays.featureIdArray.emplaceBack(lastUboIndex);
                }
            }
        }

        const projectedAnchor = globe ? globe.anchor : tileAnchor;

        arrays.placedSymbolArray.emplaceBack(projectedAnchor.x, projectedAnchor.y, projectedAnchor.z, tileAnchor.x, tileAnchor.y,
            glyphOffsetArrayStart, this.glyphOffsetArray.length - glyphOffsetArrayStart, vertexStartIndex,
            lineStartIndex, lineLength, tileAnchor.segment,
            sizeVertex ? sizeVertex[0] : 0, sizeVertex ? sizeVertex[1] : 0,
            lineOffset[0], lineOffset[1],
            writingMode,
            // placedOrientation is null initially; will be updated to horizontal(1)/vertical(2) if placed
            0,
            0,
            // The crossTileID is only filled/used on the foreground for dynamic text anchors
            0,
            associatedIconIndex,
            // flipState is unknown initially; will be updated to flipRequired(1)/flipNotRequired(2) during line label reprojection
            0
        );

        arrays.symbolInstanceIndices.push(symbolInstanceIndex);
    }

    _addNullVertices(num: number, layoutVertexArray: SymbolLayoutArray, sizeVertex: number[], globe: GlobeData,
        globeExtVertexArray: SymbolGlobeExtArray, arrays: SymbolBuffers, hasAnySecondaryIcon: boolean, segment: Segment,
        indexArray: TriangleIndexArray) {
        for (let i = 0; i < num; i++) {
            // Add 4 vertices per remaining quad with zero values
            for (let j = 0; j < 4; j++) {
                addVertex(layoutVertexArray, 0, 0, 0, 0, 0, 0, sizeVertex, false, 0, 0, 0, 0);

                if (globe) {
                    addGlobeVertex(globeExtVertexArray, 0, 0, 0, 0, 0, 0);
                    addDynamicAttributes(arrays.dynamicLayoutVertexArray, 0, 0, 0, 0);
                } else {
                    addDynamicAttributes(arrays.dynamicLayoutVertexArray, 0, 0, 0, 0);
                }

                if (hasAnySecondaryIcon) {
                    addTransitioningVertex(arrays.iconTransitioningVertexArray, 0, 0);
                }
            }

            const quadStartIndex = segment.vertexLength;
            indexArray.emplaceBack(quadStartIndex, quadStartIndex + 1, quadStartIndex + 2);
            indexArray.emplaceBack(quadStartIndex + 1, quadStartIndex + 2, quadStartIndex + 3);

            segment.vertexLength += 4;
            segment.primitiveLength += 2;

            // Add zero glyph offset
            this.glyphOffsetArray.emplaceBack(0);
        }
    }

    _commitLayoutVertex(array: StructArray, boxTileAnchorX: number, boxTileAnchorY: number, boxTileAnchorZ: number, tileAnchorX: number, tileAnchorY: number, extrude: Point) {
        array.emplaceBack(
            // pos
            boxTileAnchorX,
            boxTileAnchorY,
            boxTileAnchorZ,
            // a_anchor_pos
            tileAnchorX,
            tileAnchorY,
            // extrude
            Math.round(extrude.x),
            Math.round(extrude.y));
    }

    _addCollisionDebugVertices(box: CollisionBox, scale: number, arrays: CollisionBuffers, boxTileAnchorX: number, boxTileAnchorY: number, boxTileAnchorZ: number, symbolInstance: SymbolInstance) {
        const segment = arrays.segments.prepareSegment(4, arrays.layoutVertexArray, arrays.indexArray);
        const index = segment.vertexLength;
        const symbolTileAnchorX = symbolInstance.tileAnchorX;
        const symbolTileAnchorY = symbolInstance.tileAnchorY;

        for (let i = 0; i < 4; i++) {
            arrays.collisionVertexArray.emplaceBack(0, 0, 0, 0, 0, 0);
        }

        this._commitDebugCollisionVertexUpdate(arrays.collisionVertexArrayExt, scale, box.padding, symbolInstance.zOffset);

        this._commitLayoutVertex(arrays.layoutVertexArray, boxTileAnchorX, boxTileAnchorY, boxTileAnchorZ, symbolTileAnchorX, symbolTileAnchorY, new Point(box.x1, box.y1));
        this._commitLayoutVertex(arrays.layoutVertexArray, boxTileAnchorX, boxTileAnchorY, boxTileAnchorZ, symbolTileAnchorX, symbolTileAnchorY, new Point(box.x2, box.y1));
        this._commitLayoutVertex(arrays.layoutVertexArray, boxTileAnchorX, boxTileAnchorY, boxTileAnchorZ, symbolTileAnchorX, symbolTileAnchorY, new Point(box.x2, box.y2));
        this._commitLayoutVertex(arrays.layoutVertexArray, boxTileAnchorX, boxTileAnchorY, boxTileAnchorZ, symbolTileAnchorX, symbolTileAnchorY, new Point(box.x1, box.y2));

        segment.vertexLength += 4;

        const indexArray = arrays.indexArray as LineIndexArray;
        indexArray.emplaceBack(index, index + 1);
        indexArray.emplaceBack(index + 1, index + 2);
        indexArray.emplaceBack(index + 2, index + 3);
        indexArray.emplaceBack(index + 3, index);

        segment.primitiveLength += 4;
    }

    _addTextDebugCollisionBoxes(size: InterpolatedSize, zoom: number, collisionBoxArray: CollisionBoxArray, startIndex: number, endIndex: number, instance: SymbolInstance) {
        for (let b = startIndex; b < endIndex; b++) {
            const box: CollisionBox = collisionBoxArray.get(b);
            const scale = this.getSymbolInstanceTextSize(size, instance, zoom, b);

            this._addCollisionDebugVertices(box, scale, this.textCollisionBox, box.projectedAnchorX, box.projectedAnchorY, box.projectedAnchorZ, instance);
        }
    }

    _addIconDebugCollisionBoxes(size: InterpolatedSize, zoom: number, collisionBoxArray: CollisionBoxArray, startIndex: number, endIndex: number, instance: SymbolInstance) {
        for (let b = startIndex; b < endIndex; b++) {
            const box: CollisionBox = collisionBoxArray.get(b);
            const scale = this.getSymbolInstanceIconSize(size, zoom, instance.placedIconSymbolIndex);

            this._addCollisionDebugVertices(box, scale, this.iconCollisionBox, box.projectedAnchorX, box.projectedAnchorY, box.projectedAnchorZ, instance);
        }
    }

    generateCollisionDebugBuffers(zoom: number, collisionBoxArray: CollisionBoxArray, textScaleFactor: number) {
        if (this.hasDebugData()) {
            this.destroyDebugData();
        }

        this.textCollisionBox = new CollisionBuffers(CollisionBoxLayoutArray, collisionBoxLayout.members, LineIndexArray);
        this.iconCollisionBox = new CollisionBuffers(CollisionBoxLayoutArray, collisionBoxLayout.members, LineIndexArray);

        const iconSize = evaluateSizeForZoom(this.iconSizeData, zoom);
        const textSize = evaluateSizeForZoom(this.textSizeData, zoom, textScaleFactor);

        for (let i = 0; i < this.symbolInstances.length; i++) {
            const symbolInstance = this.symbolInstances.get(i);
            this._addTextDebugCollisionBoxes(textSize, zoom, collisionBoxArray, symbolInstance.textBoxStartIndex, symbolInstance.textBoxEndIndex, symbolInstance);
            this._addTextDebugCollisionBoxes(textSize, zoom, collisionBoxArray, symbolInstance.verticalTextBoxStartIndex, symbolInstance.verticalTextBoxEndIndex, symbolInstance);
            this._addIconDebugCollisionBoxes(iconSize, zoom, collisionBoxArray, symbolInstance.iconBoxStartIndex, symbolInstance.iconBoxEndIndex, symbolInstance);
            this._addIconDebugCollisionBoxes(iconSize, zoom, collisionBoxArray, symbolInstance.verticalIconBoxStartIndex, symbolInstance.verticalIconBoxEndIndex, symbolInstance);
        }
    }

    // The default (non-vertical, non-variable-anchor) placed text entry for an instance: whichever
    // horizontal justification was actually placed, falling back to vertical, then to -1 when the instance has no text
    defaultPlacedTextSymbolIndex(instance: SymbolInstance, boxIndex: number): number {
        return instance.rightJustifiedTextSymbolIndex >= 0 ?
            instance.rightJustifiedTextSymbolIndex : instance.centerJustifiedTextSymbolIndex >= 0 ?
                instance.centerJustifiedTextSymbolIndex : instance.leftJustifiedTextSymbolIndex >= 0 ?
                    instance.leftJustifiedTextSymbolIndex : instance.verticalPlacedTextSymbolIndex >= 0 ?
                        instance.verticalPlacedTextSymbolIndex : -1;
    }

    getSymbolInstanceTextSize(textSize: InterpolatedSize, instance: SymbolInstance, zoom: number, boxIndex: number): number {
        const symbolIndex = this.defaultPlacedTextSymbolIndex(instance, boxIndex);

        const symbol = this.text.placedSymbolArray.get(symbolIndex);
        const featureSize = evaluateSizeForFeature(this.textSizeData, textSize, symbol) / ONE_EM;

        return this.tilePixelRatio * featureSize;
    }

    getSymbolInstanceIconSize(iconSize: InterpolatedSize, zoom: number, iconIndex: number): number {
        const symbol = this.icon.placedSymbolArray.get(iconIndex);
        const featureSize = evaluateSizeForFeature(this.iconSizeData, iconSize, symbol);

        return this.tilePixelRatio * featureSize;
    }

    _commitDebugCollisionVertexUpdate(array: StructArray, scale: number, padding: number, zOffset: number) {
        array.emplaceBack(scale, -padding, -padding, zOffset);
        array.emplaceBack(scale,  padding, -padding, zOffset);
        array.emplaceBack(scale,  padding,  padding, zOffset);
        array.emplaceBack(scale, -padding,  padding, zOffset);
    }

    _updateTextDebugCollisionBoxes(size: InterpolatedSize, zoom: number, collisionBoxArray: CollisionBoxArray, startIndex: number, endIndex: number, instance: SymbolInstance, scaleFactor: number) {
        for (let b = startIndex; b < endIndex; b++) {
            const box: CollisionBox = collisionBoxArray.get(b);
            const scale = this.getSymbolInstanceTextSize(size, instance, zoom, b);
            const array = this.textCollisionBox.collisionVertexArrayExt;
            this._commitDebugCollisionVertexUpdate(array, scale, box.padding, instance.zOffset);
        }
    }

    _updateIconDebugCollisionBoxes(size: InterpolatedSize, zoom: number, collisionBoxArray: CollisionBoxArray, startIndex: number, endIndex: number, instance: SymbolInstance, iconScaleFactor: number) {
        for (let b = startIndex; b < endIndex; b++) {
            const box = (collisionBoxArray.get(b));
            const scale = this.getSymbolInstanceIconSize(size, zoom, instance.placedIconSymbolIndex);
            const array = this.iconCollisionBox.collisionVertexArrayExt;
            this._commitDebugCollisionVertexUpdate(array, scale, box.padding, instance.zOffset);
        }
    }

    updateCollisionDebugBuffers(zoom: number, collisionBoxArray: CollisionBoxArray, textScaleFactor: number, iconScaleFactor: number) {
        if (!this.hasDebugData()) {
            return;
        }

        if (this.hasTextCollisionBoxData()) this.textCollisionBox.collisionVertexArrayExt.clear();
        if (this.hasIconCollisionBoxData()) this.iconCollisionBox.collisionVertexArrayExt.clear();

        const iconSize = evaluateSizeForZoom(this.iconSizeData, zoom, iconScaleFactor);
        const textSize = evaluateSizeForZoom(this.textSizeData, zoom, textScaleFactor);

        for (let i = 0; i < this.symbolInstances.length; i++) {
            const symbolInstance = this.symbolInstances.get(i);
            this._updateTextDebugCollisionBoxes(textSize, zoom, collisionBoxArray, symbolInstance.textBoxStartIndex, symbolInstance.textBoxEndIndex, symbolInstance, textScaleFactor);
            this._updateTextDebugCollisionBoxes(textSize, zoom, collisionBoxArray, symbolInstance.verticalTextBoxStartIndex, symbolInstance.verticalTextBoxEndIndex, symbolInstance, textScaleFactor);
            this._updateIconDebugCollisionBoxes(iconSize, zoom, collisionBoxArray, symbolInstance.iconBoxStartIndex, symbolInstance.iconBoxEndIndex, symbolInstance, iconScaleFactor);
            this._updateIconDebugCollisionBoxes(iconSize, zoom, collisionBoxArray, symbolInstance.verticalIconBoxStartIndex, symbolInstance.verticalIconBoxEndIndex, symbolInstance, iconScaleFactor);
        }

        if (this.hasTextCollisionBoxData() && this.textCollisionBox.collisionVertexBufferExt) {
            this.textCollisionBox.collisionVertexBufferExt.updateData(this.textCollisionBox.collisionVertexArrayExt);
        }
        if (this.hasIconCollisionBoxData() && this.iconCollisionBox.collisionVertexBufferExt) {
            this.iconCollisionBox.collisionVertexBufferExt.updateData(this.iconCollisionBox.collisionVertexArrayExt);
        }
    }

    // Fills shared scratch boxes instead of keeping per-symbol copies of the whole CollisionBoxArray;
    // the result is only valid until the next call.
    getCollisionArrays(collisionBoxArray: CollisionBoxArray, instance: SymbolInstance): CollisionArrays {
        const c = scratchCollisionArrays;
        const hasText = instance.textBoxStartIndex < instance.textBoxEndIndex;
        const hasVerticalText = instance.verticalTextBoxStartIndex < instance.verticalTextBoxEndIndex;
        const hasIcon = instance.iconBoxStartIndex < instance.iconBoxEndIndex;
        const hasVerticalIcon = instance.verticalIconBoxStartIndex < instance.verticalIconBoxEndIndex;
        c.textBox = hasText ? scratchTextBox : undefined;
        c.verticalTextBox = hasVerticalText ? scratchVerticalTextBox : undefined;
        c.iconBox = hasIcon ? scratchIconBox : undefined;
        c.verticalIconBox = hasVerticalIcon ? scratchVerticalIconBox : undefined;
        c.textFeatureIndex = hasText ? readCollisionBox(collisionBoxArray, instance.textBoxStartIndex, scratchTextBox) : undefined;
        c.verticalTextFeatureIndex = hasVerticalText ? readCollisionBox(collisionBoxArray, instance.verticalTextBoxStartIndex, scratchVerticalTextBox) : undefined;
        c.iconFeatureIndex = hasIcon ? readCollisionBox(collisionBoxArray, instance.iconBoxStartIndex, scratchIconBox) : undefined;
        c.verticalIconFeatureIndex = hasVerticalIcon ? readCollisionBox(collisionBoxArray, instance.verticalIconBoxStartIndex, scratchVerticalIconBox) : undefined;
        return c;
    }

    hasTextData(): boolean {
        return this.text.segments.get().length > 0;
    }

    hasIconData(): boolean {
        return this.icon.segments.get().length > 0;
    }

    hasDebugData(): CollisionBuffers {
        return this.textCollisionBox && this.iconCollisionBox;
    }

    hasTextCollisionBoxData(): boolean {
        return this.hasDebugData() && this.textCollisionBox.segments.get().length > 0;
    }

    hasIconCollisionBoxData(): boolean {
        return this.hasDebugData() && this.iconCollisionBox.segments.get().length > 0;
    }

    hasIconTextFit(): boolean {
        return this.hasAnyIconTextFit;
    }

    addIndicesForPlacedSymbol(iconOrText: SymbolBuffers, placedSymbolIndex: number) {
        const placedSymbol = iconOrText.placedSymbolArray.get(placedSymbolIndex);

        const endIndex = placedSymbol.vertexStartIndex + placedSymbol.numGlyphs * 4;
        for (let vertexIndex = placedSymbol.vertexStartIndex; vertexIndex < endIndex; vertexIndex += 4) {
            iconOrText.indexArray.emplaceBack(vertexIndex, vertexIndex + 1, vertexIndex + 2);
            iconOrText.indexArray.emplaceBack(vertexIndex + 1, vertexIndex + 2, vertexIndex + 3);
        }
    }

    getSortedSymbolIndexes(angle: number): Array<number> {
        if (this.sortedAngle === angle && this.symbolInstanceIndexes !== undefined) {
            return this.symbolInstanceIndexes;
        }
        const sin = Math.sin(angle);
        const cos = Math.cos(angle);
        const rotatedYs: number[] = [];
        const featureIndexes: number[] = [];
        const result: number[] = [];

        for (let i = 0; i < this.symbolInstances.length; ++i) {
            result.push(i);
            const symbolInstance = this.symbolInstances.get(i);
            rotatedYs.push(Math.round(sin * symbolInstance.tileAnchorX + cos * symbolInstance.tileAnchorY) | 0);
            featureIndexes.push(symbolInstance.featureIndex);
        }

        result.sort((aIndex, bIndex) => (rotatedYs[aIndex] - rotatedYs[bIndex]) || (featureIndexes[bIndex] - featureIndexes[aIndex]));

        return result;
    }

    getSortedIndexesByZOffset(): Array<number> {
        if (!this.zOffsetSortDirty) {
            assert(this.symbolInstanceIndexesSortedZOffset.length === this.symbolInstances.length);
            return this.symbolInstanceIndexesSortedZOffset;
        }
        if (!this.symbolInstanceIndexesSortedZOffset) {
            this.symbolInstanceIndexesSortedZOffset = [];
            for (let i = 0; i < this.symbolInstances.length; ++i) {
                this.symbolInstanceIndexesSortedZOffset.push(i);
            }
        }
        this.zOffsetSortDirty = false;
        return this.symbolInstanceIndexesSortedZOffset.sort((aIndex, bIndex) => this.symbolInstances.get(bIndex).zOffset - this.symbolInstances.get(aIndex).zOffset);
    }

    addToSortKeyRanges(symbolInstanceIndex: number, sortKey: number) {
        const last = this.sortKeyRanges.at(-1);
        if (last && last.sortKey === sortKey) {
            last.symbolInstanceEnd = symbolInstanceIndex + 1;
        } else {
            this.sortKeyRanges.push({
                sortKey,
                symbolInstanceStart: symbolInstanceIndex,
                symbolInstanceEnd: symbolInstanceIndex + 1
            });
        }
    }

    sortFeatures(angle: number) {
        if (!this.sortFeaturesByY) return;
        if (this.sortedAngle === angle) return;

        // If the symbols are allowed to overlap sort them by their vertical screen position.
        // The index array buffer is rewritten to reference the (unchanged) vertices in the
        // sorted order.

        // Viewport-y sorting is not supported when symbols span multiple render segments.
        // Multiple segments occur when a tile has enough symbols to overflow MAX_VERTEX_ARRAY_LENGTH.
        if (this.text.segments.get().length > 1 || this.icon.segments.get().length > 1) {
            this.sortFeaturesByY = false;
            return;
        }

        // To avoid sorting the actual symbolInstance array we sort an array of indexes.
        this.symbolInstanceIndexes = this.getSortedSymbolIndexes(angle);
        this.sortedAngle = angle;

        this.text.indexArray.clear();
        this.icon.indexArray.clear();

        this.featureSortOrder = [];

        for (const i of this.symbolInstanceIndexes) {
            const symbol = this.symbolInstances.get(i);
            this.featureSortOrder.push(symbol.featureIndex);
            const {
                rightJustifiedTextSymbolIndex: right, centerJustifiedTextSymbolIndex: center,
                leftJustifiedTextSymbolIndex: left, verticalPlacedTextSymbolIndex: vertical,
                placedIconSymbolIndex: icon, verticalPlacedIconSymbolIndex: iconVertical
            } = symbol;

            // Only add a given index the first time it shows up, to avoid duplicate
            // opacity entries when multiple justifications share the same glyphs.
            if (right >= 0) this.addIndicesForPlacedSymbol(this.text, right);
            if (center >= 0 && center !== right) this.addIndicesForPlacedSymbol(this.text, center);
            if (left >= 0 && left !== center && left !== right) this.addIndicesForPlacedSymbol(this.text, left);

            if (vertical >= 0) this.addIndicesForPlacedSymbol(this.text, vertical);
            if (icon >= 0) this.addIndicesForPlacedSymbol(this.icon, icon);
            if (iconVertical >= 0) this.addIndicesForPlacedSymbol(this.icon, iconVertical);
        }

        if (this.text.indexBuffer) this.text.indexBuffer.updateData(this.text.indexArray);
        if (this.icon.indexBuffer) this.icon.indexBuffer.updateData(this.icon.indexArray);
    }

}

register(SymbolBucket, 'SymbolBucket', {
    omit: ['layers', 'collisionBoxArray', 'compareText', 'features', 'projectionInstance', 'tileTransformInstance']
});

SymbolBucket.addDynamicAttributes = addDynamicAttributes;

export default SymbolBucket;
export {addDynamicAttributes, updateGlobeVertexNormal};
